// Port of src/app/components/nav-bar/nav-bar.component.{ts,html,scss}
// (mypart-nav-bar) — the orchestrator.
//
// Lifecycle / API mapping:
// - constructor effect()s → useEffect with the exact tracked signals as deps
//   (see the numbered E1..E9 comments below).
// - ngOnInit → the init mount effect (StrictMode-guarded). The Angular
//   Router NavigationEnd subscription: handleNavigationEnd() runs once for the
//   initial URL in the init effect, and again on every client-side route
//   change via the `routeKey` prop (App passes the react-router location.key,
//   which changes per navigation — including query-string-only navigations,
//   matching NavigationEnd).
// - ngAfterViewInit → prompterText effect (the prompterText$ BehaviorSubject
//   subscription; the store selector + useEffect replicates the initial
//   emission and distinctUntilChanged) + addPlaceholder mount effect. Effect
//   declaration order mirrors the Angular execution order (init → prompter
//   subscription initial emission → addPlaceholder → constructor effects).
// - inputEvents Subject pipe(debounceTime(500), distinctUntilChanged(), tap)
//   → debounce() + lastInputValueRef + the tap logic inlined at the top of
//   the subscriber.
// - CDK Overlay (FilterDropdown / MetadataFilterDropdown / MenuDropdown) →
//   `openDropdown` state rendering the ported portal components; the Angular
//   setPositionOffScreen/Left/Right/Center calls become the `position` style
//   (including the open-then-position-after-timeout sequence).
// - PrompterAnimationService → PrompterTransition instance (useRef, Pulse &
//   Assemble). NOTE(port): @gsap/react's useGSAP was evaluated and
//   intentionally not used — the prompter tweens are created inside
//   requestAnimationFrame / setTimeout (outside any render-coupled callback),
//   so gsap.context would not capture them; cleanup is handled by stop() on
//   unmount, matching the service lifetime in Angular.
// - SafeHtml/DomSanitizer is gone — prompterText stays a plain HTML string;
//   it is written into the contenteditable div imperatively (innerHTML),
//   exactly like the Angular code did (the div is uncontrolled in React:
//   static initial children + suppressContentEditableWarning).
// - TotalActiveFiltersPipe → totalActiveFiltersPipe() (inlined).
// - The `| async`-free template expression `editableDiv.innerText !== ...`
//   (clear-search icon) is re-evaluated per render via the ref — the same
//   state updates that change the div content also re-render the component
//   (mirrors Angular's per-CD evaluation).
//
// Intentionally inlined/merged methods (vs the Angular class):
// - htmlToPlainText / moveCursorToEnd were one-line delegations to
//   SearchInputHelperService → the search-input-helper module functions are
//   called directly.
// - createOverlay()/attachDropdown() collapse into openFilterDropdown /
//   openMenuDropdown + the openDropdown state (the backdrop click and
//   outsideDropdownClick wiring live in the ported dropdown components).
// - NOTE(port): mypart-filters-container is NOT rendered by the Angular
//   nav-bar template (it is only declared in the module) — same here.
// - NOTE(port): three Angular conditions tested NavBarService signal
//   REFERENCES instead of calling them (`!this.navBarService.uploadStarted`
//   without parens in resetFile()/checkIfIsHomePage()) — those branches never
//   executed at runtime and are preserved as dead branches with comments.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ClipboardEvent as ReactClipboardEvent,
  CSSProperties,
  FormEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  DragEvent as ReactDragEvent
} from 'react';

import Background from './Background';
import Logo from './Logo';
import PlaylistIcon from './PlaylistIcon';
import LoginMenu from './LoginMenu';
import Subtitle from './Subtitle';
import Search from './Search';
import FileUpload from './FileUpload';
import CuratedCarousel from './CuratedCarousel';
import UploadResponses from './UploadResponses';
import FilterDropdown from './FilterDropdown';
import MetadataFilterDropdown from './MetadataFilterDropdown';
import MenuDropdown from './MenuDropdown';
import Popover from '../../shared/components/Popover';
import SocialSharing from '../../shared/components/SocialSharing';
import Tooltip from '../../shared/components/Tooltip';

import { getDefaultNoMoreResultsMessage, useGlobalStore } from '../../stores/global-store';
import { useView, getView } from '../view/use-view';
import { useFiltersStore } from '../../stores/filters-store';
import { useUserStore } from '../../stores/user-store';
import { useNavBarStore } from '../../stores/navbar-store';
import { useLoaderStore } from '../../stores/loader-store';
import { useDeviceStore } from '../../stores/device-store';
import { useModalStore } from '../../stores/modal-store';
import { useToastStore } from '../../stores/toast-store';
import { usePlayerStore } from '../../stores/player-store';
import {
  cancelRequests,
  constructFiltersAndNavigate,
  freeTextQuery,
  getArtwork,
  getQuickTags,
  getResultsOs,
  getResultsOsFamilies,
  getSearchOrigin,
  sendEventToServer,
  setSearchOrigin,
  uploadFile as uploadFileRequest,
  useHttpReqStore
} from '../../api/http-req';
import { navigate } from '../../app/navigation';
import { resetNavigateDedup } from '../../api/navigate-dedup';
import { fetchSongsByQuery } from '../../queries/songs';
import { environment } from '../../config/environment';
import { ModalType } from '../../interfaces/modal-types.enum';
import type { Artwork } from '../../interfaces/artwork';
import type { Filter } from '../../interfaces/filter';
import type { SortBy } from '../../interfaces/sort-by';
import type { FinalSong } from '../../interfaces/final-song';
import type { PrompterResult } from '../../interfaces/prompter-result';
import type { CuratedPlaylist } from '../../interfaces/curated-playlist';
import { getPositionForDropdown, isElementInView } from '../../shared/utils/dropdown.helper';
import { logger } from '../../shared/logging/logger';
import { shuffle } from '../../shared/utils/nav-bar-helper';
import { useOutsideClick } from '../../shared/hooks/useOutsideClick';
import { useIsEllipsis } from '../../shared/hooks/useIsEllipsis';
import { debounce } from '../../shared/utils/debounce';
import { PrompterTransition } from './prompter-transition';
import {
  addPlaceholder as addPlaceholderHelper,
  getQueryTextExcludingCatalogTier,
  htmlToPlainText,
  isUrl as isUrlHelper,
  containsForbiddenWords,
  moveCursorToEnd,
  removePlaceholder as removePlaceholderHelper,
  setContentEditable as setContentEditableHelper,
  truncateFileName as truncateFileNameHelper
} from './search-input-helper';
import { PIXELATE_IN_OUT_ENTER_CLASS } from '../../animations/pixelateInOut';
import { PIXELATE_IN_OUT_DELAY_ENTER_CLASS } from '../../animations/pixelateInOutDelay';
import { expandFadeInEnter } from '../../animations/expandFadeInAnimation';
import {
  decidePopstateAction,
  extractLastSegment,
  isImageOrVideoFile,
  noMoreResultsMessageFor,
  routeHasQueryParams,
  totalActiveFiltersPipe,
} from './nav-bar-logic';
import { parsePrompterHtml } from './search/parse-prompter-html';
import { segmentsToHtml } from './search/segments-to-html';
import type { PromptSegment } from './search/prompt-segment';
import { chipAdjacentToCaret, armChip, disarmChip, removeChip } from './search/chip-edit';
import './NavBar.scss';

const TOTAL_REQUESTS_BEFORE_POPUP = 3;
const MAX_QUERY_LENGTH = 150;
const defaultSearchPlaceholder = 'Add a text description, song title, artist name, image, or video.';

// NgOptimizedImage [fill] replacement (same as Logo/PlaylistIcon).
const fillStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%'
};

const sortImgAlt = 'The image shows an icon designed for sorting functionality. ' +
  'It features three horizontal red bars of varying lengths, stacked vertically, with a red arrow' +
  ' pointing downward on the right side of the bars. Each element has a white outline, making the ' +
  'icon stand out against a black background. The three bars represent sortable items, and the ' +
  'downward-pointing arrow indicates the sorting direction, likely descending. This design is clear ' +
  'and intuitive, effectively conveying its purpose as a sorting button.';

// NOTE(port): totalActiveFiltersPipe and extractLastSegment are pure helpers
// moved to ./nav-bar-logic (imported above).

/**
 * Angular signal() replacement for component-local signals that are BOTH
 * rendered and read synchronously inside handlers/async flows: the setter
 * writes the ref immediately (signal semantics — readable before the next
 * render) and schedules the state update for rendering.
 */
function useSignalState<T>(initial: T): [T, (value: T) => void, () => T] {
  const [value, setValue] = useState<T>(initial);
  const ref = useRef<T>(initial);
  const set = useCallback((v: T) => {
    ref.current = v;
    setValue(v);
  }, []);
  const get = useCallback(() => ref.current, []);
  return [value, set, get];
}

/** One sort entry of the tippy sort dropdown ([isEllipsis] → useIsEllipsis). */
function SortByItem({ sortBy, onSelect }: { sortBy: SortBy; onSelect: (sortBy: SortBy) => void }) {
  const { ref } = useIsEllipsis<HTMLSpanElement>();

  return (
    <li onClick={(e) => {
      e.stopPropagation();
      onSelect(sortBy);
    }}
      className="cursor-pointer">
      <div className="flex items-center pl-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600">
        <span ref={ref}
          className={`w-full py-2 ml-2 text-sm font-medium text-gray-900 rounded
                            max-[600px]:whitespace-normal dark:text-gray-300 ellipsis${sortBy.selected ? ' !text-green-500 underline' : ''}`}>
          {sortBy.name}
        </span>
      </div>
    </li>
  );
}

/** Which CDK-overlay replacement is open (Angular OverlayRef + ComponentPortal). */
type OpenDropdown =
  | { kind: 'filter'; filter: Filter; isInputFilter: boolean; position: CSSProperties }
  | { kind: 'metadata'; isInputFilter: boolean; position: CSSProperties }
  | { kind: 'menu'; position: CSSProperties };

export interface NavBarProps {
  /**
   * Changes on every client-side navigation (App passes react-router's
   * location.key) — re-runs handleNavigationEnd, the port of the Angular
   * Router NavigationEnd subscription. The initial URL is handled by the init
   * mount effect.
   */
  routeKey?: string;
}

export default function NavBar({ routeKey }: NavBarProps) {
  // --- store subscriptions (Angular service signal aliases) ---
  useFiltersStore(s => s.filtersVersion); // re-render on in-place filter-tree mutations
  const isLoading = useLoaderStore(s => s.isLoading);
  const isMobileDevice = useDeviceStore(s => s.isMobileDevice);
  const user = useUserStore(s => s.user);
  const searchDropDownHidden = useNavBarStore(s => s.searchDropDownHidden);
  const lockedFilters = useFiltersStore(s => s.lockedFilters);
  const filters = useFiltersStore(s => s.filters);
  const metadataFilters = useFiltersStore(s => s.metadataFilters);
  const sortByArray = useFiltersStore(s => s.sortByArray);
  const lyricsContains = useFiltersStore(s => s.lyricsContains);
  const filtersVisible = useFiltersStore(s => s.filtersVisible);
  const prompterText = useFiltersStore(s => s.prompterText);
  const artistFromUrl = useHttpReqStore(s => s.artistFromUrl);
  const songFromUrl = useHttpReqStore(s => s.songFromUrl);
  // Derived view replaces the old showBigSearchHomePage store flag. Kept under the
  // same local name so the JSX render gates below are unchanged; it is now a pure
  // function of the URL (+ the critical-error override), never `undefined`.
  const view = useView();
  const showBigSearchHomePage = view === 'home';
  const appTheme = useGlobalStore(s => s.appTheme);
  const discoveryMode = useGlobalStore(s => s.discoveryMode);
  const playAllSongs = useGlobalStore(s => s.playAllSongs);
  const initialLoadingProcessComplete = useGlobalStore(s => s.initialLoadingProcessComplete);
  const loadMoreSongsEvent = useGlobalStore(s => s.loadMoreSongsEvent);
  const scrollEventOnMobile = useGlobalStore(s => s.scrollEventOnMobile);
  const curatedPlaylists = useGlobalStore(s => s.curatedPlaylists);
  const referenceSongFromUrl = useGlobalStore(s => s.referenceSongFromUrl);
  const updateGlobalStateSignal = useGlobalStore(s => s.updateGlobalStateSignal);
  const songs = useGlobalStore(s => s.songs);
  // totalPlaylistSongs computed
  const totalPlaylistSongs = useGlobalStore(s => s.playlistSongs.size);
  // NavBarService signal aliases
  const fileUploadResponse = useNavBarStore(s => s.fileUploadResponse);
  const file = useNavBarStore(s => s.file);
  const uploadStarted = useNavBarStore(s => s.uploadStarted);
  const uploadFailed = useNavBarStore(s => s.uploadFailed);
  const fileUploadComplete = useNavBarStore(s => s.fileUploadComplete);
  const uploadError = useNavBarStore(s => s.uploadError);

  // --- component-local signals ---
  const [showNothingFound, setShowNothingFound] = useState(false);
  const [curatedPromptPlaylists, setCuratedPromptPlaylists, getCuratedPromptPlaylists] =
    useSignalState<CuratedPlaylist[] | undefined>(undefined);
  const [showPromptCuratedPlaylists, setShowPromptCuratedPlaylists] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [prompterIsActive, setPrompterIsActive, getPrompterIsActive] = useSignalState(false);
  const [errorMessageFromPrompt, setErrorMessageFromPrompt, getErrorMessageFromPrompt] =
    useSignalState<string | undefined>(undefined);
  const [promptFromNewHomeScreen, setPromptFromNewHomeScreen, getPromptFromNewHomeScreen] =
    useSignalState<string | undefined>(undefined);
  // Plain class fields that the template renders (OnPush re-evaluated them):
  const [artworks, setArtworks, getArtworks] = useSignalState<Artwork[]>([]);
  const [showLinkHasBeenEntered, setShowLinkHasBeenEntered, getShowLinkHasBeenEntered] = useSignalState(false);
  const [showScrollLeft, setShowScrollLeft] = useState(false);
  const [showScrollRight, setShowScrollRight] = useState(false);
  const [isQuickTags, setIsQuickTags, getIsQuickTags] = useSignalState(true);
  const [isTextInsideSearch, setIsTextInsideSearch, getIsTextInsideSearch] = useSignalState(false);

  // --- plain (non-rendered) class fields → refs ---
  const currentRequestsBeforePopupRef = useRef(0);
  const activeUrlLocationRef = useRef('');
  const manuallySelectedArtworkRef = useRef(false); // set, never read (same as Angular)
  const exactMatchToastShownRef = useRef(false);
  const clearSearchClickedRef = useRef(false);
  const prompterEnterPressedOccurredRef = useRef(false);
  const lastFilterClickedRef = useRef<string | null>(null);
  const lastInputValueRef = useRef<string | undefined>(undefined); // distinctUntilChanged
  const currentSearchStrRef = useRef('');
  const currentFileNameRef = useRef('');
  const stopGetResultsFinalFlowRef = useRef(false);
  const artworkFromUrlRef = useRef<Artwork | undefined>(undefined);
  const currentShowBigSearchHomePageValueRef = useRef(false);
  const addTopTwentySongsToPlaylistFromPromptRef = useRef(false);
  const addedTopTwentySongsToPlaylistFromFirstSearchRef = useRef(false);
  const tempPrompterResultRef = useRef<PrompterResult | undefined>(undefined);
  // True from the moment a prompter Enter search begins until the GSAP swap
  // renders the chips. While true, setSearchDivValue is a no-op, so the bar keeps
  // showing the user's typed text untouched — no early wipe from removeAllFilters,
  // no premature plain-chip render from the prompterText effect. The Pulse &
  // Assemble transition is then the sole thing that changes the bar: it pops the
  // typed words out, then springs the chips in.
  const prompterSwapPendingRef = useRef(false);

  // --- @ViewChild refs ---
  const navbarRef = useRef<HTMLElement | null>(null);
  const editableDivRef = useRef<HTMLDivElement | null>(null);
  // The chip currently "armed" for deletion (first Backspace/Delete highlights
  // it; the second press removes it). Cleared by any other key, click, or input.
  const armedChipRef = useRef<HTMLElement | null>(null);
  const searchDropdownRef = useRef<HTMLDivElement | null>(null);
  const filtersScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [sortOpen, setSortOpen] = useState(false);

  // PrompterTransition instance (Pulse & Assemble).
  const prompterTransitionRef = useRef<PrompterTransition | null>(null);
  if (!prompterTransitionRef.current) {
    prompterTransitionRef.current = new PrompterTransition();
  }
  const prompterTransition = prompterTransitionRef.current;

  // CDK Overlay replacement state.
  const [openDropdown, setOpenDropdown] = useState<OpenDropdown | null>(null);

  // True whenever the editable div currently shows an injected prompt (chips).
  // Used as the "is a prompt showing" signal for derived UI (e.g. hasSearchContent).
  // The div content itself is browser-owned.
  const [hasPrompt, setHasPrompt] = useState(false);

  // Render the prompt HTML (the store's prompterText) as imperative,
  // browser-owned chip DOM. Empty html clears the div.
  function renderPromptIntoDiv(html: string): void {
    const div = editableDivRef.current;
    if (!div) return;
    if (html) {
      div.innerHTML = segmentsToHtml(parsePrompterHtml(html));
      div.classList.remove('placeholder');
      setHasPrompt(true);
    } else {
      div.innerHTML = '';
      setHasPrompt(false);
    }
  }

  // Render a prompt built from explicit segments (the deep-link title path).
  function renderSegmentsIntoDiv(segments: PromptSegment[]): void {
    const div = editableDivRef.current;
    if (!div) return;
    div.innerHTML = segmentsToHtml(segments);
    div.classList.remove('placeholder');
    setHasPrompt(true);
  }

  // Clear the (browser-owned) editable div to empty.
  function clearEditableDivToEmpty() {
    if (editableDivRef.current) {
      editableDivRef.current.innerText = '';
      editableDivRef.current.innerHTML = '';
      editableDivRef.current.classList.remove('placeholder');
    }
    setHasPrompt(false);
  }

  // ===========================================================================
  // Methods (function declarations — hoisted, so effects above/below can call)
  // ===========================================================================

  function toggleDiscoveryMode() {
    getResults();
  }

  function onDivInput(event: FormEvent<HTMLDivElement>): void {
    disarmCurrentChip();
    const inputType = (event.nativeEvent as InputEvent).inputType;
    if (editableDivRef.current!.innerText.trim() === '' &&
      (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward')) {
      return;
    }

    const value: string = (event.target as HTMLElement).innerText;

    if (!editableDivRef.current!.querySelector('[data-chip]') && value.length > MAX_QUERY_LENGTH) {
      // Truncate the text to the max length
      editableDivRef.current!.innerText = value.substring(0, MAX_QUERY_LENGTH);
      // Move the cursor to the end
      moveCursorToEnd(event.target as HTMLElement);
      return;
    }

    inputEventsNext(value);
  }

  // inputEvents Subject subscriber (after debounceTime(500) + distinctUntilChanged + tap).
  function handleDebouncedInput(value: string): void {
    // distinctUntilChanged
    if (lastInputValueRef.current === value) {
      return;
    }
    lastInputValueRef.current = value;

    // tap
    if (currentSearchStrRef.current !== value) {
      setPrompterIsActive(false);
    }

    // subscribe body
    if (isUrl(value)) {
      return;
    }
    if (useNavBarStore.getState().file) {
      uploadFile();
      return;
    }

    artworkFromUrlRef.current = undefined;
    setIsTextInsideSearch(value.trim() !== '');
    if (getArtworks().length > 0) {
      const artwork = useFiltersStore.getState().selectedArtwork;
      if (`${artwork?.artist} - ${artwork?.title}` !== value) {
        setArtworks([]);
        hideSearchDropdown(true, 'input:artwork-mismatch');
        manuallySelectedArtworkRef.current = false;
      }
    }
    if (value.trim() === '') {
      resetPrompter();
    }

    // Check if the user has deleted all text or value is null
    if (value && value.trim()) {
      // Handle valid value
      setShowNothingFound(false);
      getSongs(value);
      hideSearchDropdown(false, 'input:typed-text');
    } else {

    }
  }

  function onFiltersScroll() {
    if (filtersScrollContainerRef.current) {
      setTimeout(() => {
        const container = filtersScrollContainerRef.current;
        if (!container) {
          return;
        }
        const scrollLeftPos = container.scrollLeft;
        const scrollRightPos = container.scrollWidth - container.clientWidth - scrollLeftPos;

        setShowScrollLeft(scrollLeftPos > 0);
        setShowScrollRight(scrollRightPos > 1);
      });
    }
  }

  function scroll(direction: 'left' | 'right'): void {
    if (filtersScrollContainerRef.current) {
      const container = filtersScrollContainerRef.current;

      // Define the amount to scroll in pixels
      const scrollAmount = 100;

      // Calculate the new scroll position
      let newScrollLeft: number;

      if (direction === 'left') {
        newScrollLeft = container.scrollLeft - scrollAmount;
        // Ensure we don't scroll past the start
        if (newScrollLeft < 0) {
          newScrollLeft = 0;
        }
      } else {
        newScrollLeft = container.scrollLeft + scrollAmount;
        // Ensure we don't scroll past the end
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        if (newScrollLeft > maxScrollLeft) {
          newScrollLeft = maxScrollLeft;
        }
      }

      // Scroll the container to the new position
      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });

      // Update any scroll-dependent logic
      onFiltersScroll();
    }
  }

  function outsideClickEvent() {
    hideSearchDropdown(true, 'outside-click');
  }

  function showSearchDropDown() {
    closeOverlay();
    if (getIsTextInsideSearch()) {
      if (!getErrorMessageFromPrompt() && !getShowLinkHasBeenEntered()) {
        if (getArtworks().length === 0) {
          setShowNothingFound(true);
        }
        hideSearchDropdown(false, 'show-search-dropdown');
      }
    }
  }

  function resetData(sortByChanged = false) {
    useGlobalStore.getState().resetPageNumber();
    if (!sortByChanged) {
      useFiltersStore.getState().resetSelectedFilters();
    }
  }

  function selectArtwork(artwork: Artwork) {
    const filtersStore = useFiltersStore.getState();
    useGlobalStore.getState().setGlobalField('artistPages', -1);
    filtersStore.setMultipleArtworksIds(undefined);
    if (getPrompterIsActive()) {
      changeArtworkInPrompter(artwork);
      return;
    }
    if (!filtersStore.filtersVisible) {
      toggleFilters();
    }
    const selected = filtersStore.selectedArtwork;
    if (selected?.id === artwork.id && !getIsQuickTags()) {
      hideSearchDropdown(true, 'select-artwork:already-selected');
      return;
    }

    filtersStore.changeSelectedArtwork(artwork);

    setSearchDivValue(undefined);
    manuallySelectedArtworkRef.current = true;
    hideSearchDropdown(true, 'select-artwork');
    setIsQuickTags(false);

    resetData();

    useFiltersStore.getState().resetSortBy();
    useFiltersStore.getState().resetFilters();

    constructFiltersAndNavigate();
  }

  function getResults(isLoadMore = false, comesFromSort = false) {
    setTimeout(() => {
      Promise.resolve().then(() => {
        if (getView() !== 'home') {
          const httpReqStore = useHttpReqStore.getState();
          const searchInArtist = httpReqStore.artistFromUrl;
          const referenceSongIds = httpReqStore.songFromUrl;

          if (searchInArtist) {
            httpReqStore.setClearArtistFromUrl(false);
          }
          if (referenceSongIds) {
            httpReqStore.setClearSongFromUrl(false);
          }

          if (comesFromSort && artworkFromUrlRef.current) {
            httpReqStore.setClearSongFromUrl(true);
            artworkFromUrlRef.current = undefined;
          }

          useGlobalStore.getState().setGlobalField('playAllSongs', false);
          if (!isLoadMore) {
            usePlayerStore.getState().setSongAndPosition(undefined, undefined);
            useGlobalStore.getState().setGlobalField('highlightedSong', undefined);
          }
          let selectedId =
            useFiltersStore.getState().multipleArtworksIds || useFiltersStore.getState().selectedArtwork?.id.toString();

          if (referenceSongIds) {
            selectedId = referenceSongIds;
          }

          useGlobalStore.getState().setSongs([]);
          // Use a microtask
          Promise.resolve().then(() => {
            stopGetResultsFinalFlowRef.current = false;
            const req = selectedId
              ? getResultsOs(selectedId)
              : getQuickTags(searchInArtist);
            req
              .then((finalSongs) => {
                if (!stopGetResultsFinalFlowRef.current) {
                  startFinalSongsFlow(finalSongs, isLoadMore, selectedId!);
                }
              })
              .catch((err: any) => {
                console.log('Get Results Error:');
                let logError = true;
                if (err.name) {
                  console.log(err.name);
                  logError = false;
                }
                if (err.message) {
                  console.log(err.message);
                  logError = false;
                }
                if (logError) {
                  console.log(err);
                }

                if (err.name && err.name !== 'EmptyError' && err.name !== 'Cancelled') {
                  useGlobalStore.getState().setSongsLoading(false);
                } else if (err.message === 'results is undefined') {
                  useGlobalStore.getState().setSongsLoading(false);
                  useGlobalStore.getState().setGlobalField('noMoreResultsMessage',
                    'We have some problems with the search. Try tweaking your search.');
                  useGlobalStore.getState().setGlobalField('showNoMoreResults', true);
                }
              })
              .finally(() => {
                setPromptFromNewHomeScreen(undefined);
                checkRequestsForPopup();
                if (searchInArtist) {
                  useHttpReqStore.getState().setClearArtistFromUrl(true);
                }
                if (referenceSongIds) {
                  useHttpReqStore.getState().setClearSongFromUrl(true);
                }
              });
          });
        }
      }
      );
    }, 10);
  }

  function loadMore() {
    useGlobalStore.getState().addPageNumber();
    getResults(true);
  }

  function openFilterDropdown(filterElement: HTMLElement, filter: Filter, isMetadata = false, isInputFilter = false) {
    const isSameFilter = lastFilterClickedRef.current === filter.title;

    // Angular: `if (this.overlayRef && isSameFilter) return;` — overlayRef was
    // always truthy (created in the constructor), so only isSameFilter matters.
    if (isSameFilter) {
      return;
    }
    lastFilterClickedRef.current = filter.title;

    // Create the overlay (Angular createOverlay() disposed the previous one
    // first, which also nulled lastFilterClicked — sequence preserved).
    closeOverlay();

    // Check if the filter element is in view.
    // NOTE(port): for input-filter spans opened while the filters bar is not
    // rendered, Angular dereferenced filtersScrollContainer! blindly; here we
    // fall back to document.body instead of throwing.
    const container = filtersScrollContainerRef.current ?? document.body;
    const isInView = isElementInView(container, filterElement);
    const isMobile = useDeviceStore.getState().isMobileDevice;

    if (!isInputFilter && (!isInView || isMobile)) {
      // Scroll the filter element into view
      filterElement.parentElement!.scrollTo({ left: filterElement.offsetLeft, behavior: 'smooth' });
    }

    // Attach the dropdown with the initial off-screen position (Angular
    // setPositionOffScreen) to avoid visual glitch.
    const offScreen: CSSProperties = { top: '-9999px', left: '-9999px' };
    setOpenDropdown(isMetadata
      ? { kind: 'metadata', isInputFilter, position: offScreen }
      : { kind: 'filter', filter, isInputFilter, position: offScreen });

    const timeout = isInputFilter ? 0 : isMobile ? 300 : isInView ? 0 : 300;
    setTimeout(() => {
      const rect = filterElement.getBoundingClientRect();
      const dropdownPosition = getPositionForDropdown(container, filterElement);

      let position: CSSProperties;
      if (isMetadata && !isInputFilter) {
        // setPositionLeft
        position = { top: rect.bottom, left: rect.left };
      } else {
        switch (dropdownPosition) {
          case 'left':
            // setPositionRight
            position = { top: rect.bottom, right: window.innerWidth - rect.right, left: 'unset' };
            break;
          case 'right':
            // setPositionLeft
            position = { top: rect.bottom, left: rect.left };
            break;
          default: {
            // setPositionCenter (assuming the dropdown width is 250px)
            const centerLeft = rect.left + (rect.width / 2) - (250 / 2);
            position = { top: rect.bottom, left: centerLeft };
          }
        }
      }

      setOpenDropdown(prev => (prev ? { ...prev, position } : prev));
    }, timeout);
  }

  function openMenuDropdown(menu: HTMLElement) {
    closeOverlay();

    const rect = menu.getBoundingClientRect();
    // component.instance.setPosition(rect.bottom, 0)
    setOpenDropdown({ kind: 'menu', position: { top: rect.bottom, left: 0 } });
  }

  function selectSortBy(sortBy: SortBy) {
    setSortOpen(false);
    if (artworkFromUrlRef.current) {
      useFiltersStore.getState().changeSelectedArtwork(artworkFromUrlRef.current);
    }
    useFiltersStore.getState().changeSelectedSort(sortBy);
    resetData(true);
    getResults(false, true);
  }

  function clearSubFiltersFromFilter(filter: Filter) {
    if (filter.title === 'Lyrical Theme') {
      useFiltersStore.getState().setLyricsContains(undefined);
    }
    useFiltersStore.getState().clearSubFiltersFromFilter(filter);
    filtersClickedEvent();
    // Re-render the prompt chips from the (regenerated) prompterText as React JSX.
    renderPromptIntoDiv(useFiltersStore.getState().prompterText ?? '');
  }

  // NOTE(port): like Angular, the parameter is ignored — the store's
  // metadataFilters are cleared.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function clearSubFiltersFromMetadata(_filters: Filter[]) {
    useFiltersStore.getState().metadataFilters.forEach((f) => {
      if (f.totalActiveFilters > 0) {
        clearSubFiltersFromFilter(f);
      }
    });
  }

  function toggleFilters() {
    const filtersStore = useFiltersStore.getState();
    filtersStore.setFiltersVisible(!filtersStore.filtersVisible);
    if (useFiltersStore.getState().filtersVisible) {
      setTimeout(() => {
        onFiltersScroll();
      });
    }
  }

  function addPlaceholder(): void {
    addPlaceholderHelper(editableDivRef.current!, defaultSearchPlaceholder);
  }

  function removePlaceholder(): void {
    if (editableDivRef.current) {
      removePlaceholderHelper(editableDivRef.current, defaultSearchPlaceholder);
    }
  }

  // NOTE(port): `searchWillFollow` suppresses the interim navigate('/') below.
  // When a brand-new prompter search is about to run from the results page, the
  // search itself navigates to the new results URL (constructFiltersAndNavigate).
  // Eagerly navigating to '/' here first caused a visible bounce through the home
  // page (Angular hid this via zoned change-detection batching; React Router
  // paints each navigation immediately). The error paths in prompterEnterPressed
  // restore the "land on home" fallback for a failed/ununderstood query.
  function clearSearchBar(getResultsAfter = false, text = '', searchWillFollow = false) {
    const isInHomePage = window.location.pathname === '/' && window.location.search === '';
    clearSearchClickedRef.current = true;
    const navBarStore = useNavBarStore.getState();
    if (isInHomePage) {
      navBarStore.resetFileState();
      // Preserve a live visual-search session (uploaded media + its 3 prompt
      // bubbles) when a bubble/upload search is being run, so a browser Back from
      // the results can soft-restore it — the SAME guard the results-page branch
      // below uses. NOTE(base-hazard): in prod the home path is '/songhunt/', so
      // `isInHomePage` (pathname === '/') is false and this branch isn't taken
      // (the non-home branch already guards the session). In dev the home path is
      // '/', so without this guard a bubble search wiped fileUploadResponse and
      // Back fell to the hard `reload-base` popstate path instead of show-home-soft.
      if (getSearchOrigin() !== 'photo_upload' && getSearchOrigin() !== 'video_upload') {
        navBarStore.setNavBarField('fileUploadResponse', undefined);
        navBarStore.setNavBarField('imagePreview', undefined);
        navBarStore.setNavBarField('videoPreview', undefined);
      }
      resetAllToStart(text);
      return;
    }
    // When a prompter search will follow (searchWillFollow), this function does NOT
    // navigate (see the !searchWillFollow guard below) — bridge to the upcoming
    // results URL with the override so the view doesn't flicker to home meanwhile.
    // (When it DOES navigate, the override would be cleared on that navigation, so
    // setting it there would be a no-op — hence the guard.)
    if (searchWillFollow) {
      useGlobalStore.getState().setViewOverride('results');
    }
    removeAllFilters(searchWillFollow);
    useGlobalStore.getState().resetPageNumber();
    clearSearchClickedRef.current = true;

    if (getSearchOrigin() !== 'photo_upload' && getSearchOrigin() !== 'video_upload') {
      navBarStore.setNavBarField('fileUploadResponse', undefined);
      navBarStore.setNavBarField('imagePreview', undefined);
      navBarStore.setNavBarField('videoPreview', undefined);
    }

    resetAllToStart(text);
    useFiltersStore.getState().setSelectedArtwork(undefined);
    if (!searchWillFollow) {
      navigate('/');
    }
    Promise.resolve().then(() => {
      // When a prompter search will follow it fires its own (correctly scoped)
      // getResults after rebuilding the filters — skip this interim search so we
      // don't momentarily query the full catalog with the just-reset filters.
      if (getResultsAfter && !searchWillFollow) {
        getResults();
      }
    });
  }

  // NOTE(port): The X button used to call clearSearchBar(true), which was a full
  // reset (remove all filters, reset sort/page/prompter/file state, navigate to
  // '/', and re-fetch results). Per product change, the X now ONLY clears the
  // search input text/content and restores the placeholder — it does not touch
  // filters, sort, navigation, or trigger a new search.
  function clearSearchText() {
    setContentEditable(true);
    clearEditableDivToEmpty();
    addPlaceholder();
    // Re-render so the X / search icons re-evaluate against the now-empty input.
    setIsTextInsideSearch(false);
  }

  function addTopTwentySongsToPlaylist(origin: string) {
    useGlobalStore.getState().addTopTwentySongsToPlaylist();

    const currentUser = useUserStore.getState().user;

    sendEventToServer(origin, {
      'email': currentUser ? currentUser.email : 'no user',
      'artworkIds': useGlobalStore.getState().getArtworkIdsFromPlaylist(),
      'url': window.location.href
    });

    if (useGlobalStore.getState().totalPlaylistSongs > 80) {
      useToastStore.getState().warning('You can add a maximum of 100 songs to a playlist');
      return;
    }
  }

  function getSearchLink(): string {
    return window.location.href;
  }

  function submitSong() {
    const currentUser = useUserStore.getState().user;
    if (!currentUser) {
      openUpgradeLogin('Sign Up for FREE to search for ANY song, even if it\'s not in our library', false);
      return;
    }

    if (currentUser.isSubscribed || currentUser.uploadedArtworkIds.length < 3) {
      useModalStore.getState().open(ModalType.YOUTUBE_SUGGESTIONS, {
        inputs: {
          searchStr: editableDivRef.current!.innerText
        }
      });
    } else {
      useModalStore.getState().open(ModalType.LOGIN_REGISTER, {});
    }
  }

  function removeAllFilters(skipNavigate = false) {
    resetPrompter();
    setSearchDivValue('');
    useFiltersStore.getState().resetSelectedFilters();
    useFiltersStore.getState().resetSortBy();
    // When a prompter search is about to follow, it rebuilds the filters and
    // navigates to the real results URL — skip the interim navigate-to-empty so
    // we don't flash the home page in between.
    if (!skipNavigate) {
      constructFiltersAndNavigate();
    }
  }

  function togglePlayAllSongs() {
    const globalStore = useGlobalStore.getState();
    if (globalStore.playAllSongs) {
      globalStore.setGlobalField('playAllSongs', false);
      useToastStore.getState().info('Play all songs is not active');
    } else {
      globalStore.setGlobalField('playAllSongs', true);
      useToastStore.getState().info('Play all songs is active');
    }
  }

  function onPaste(event: ReactClipboardEvent<HTMLDivElement>) {
    // Prevent the default paste behavior
    event.preventDefault();

    // Get clipboard data
    const clipboardData = event.clipboardData || (window as any).clipboardData;

    if (!clipboardData) {
      return;
    }

    // Check for image/video files first
    if (clipboardData.files && clipboardData.files.length > 0) {
      const filesArray = Array.from(clipboardData.files as FileList);
      const imageVideoFiles = filesArray.filter((f: File) => isImageOrVideoFile(f));

      if (imageVideoFiles.length > 0) {
        // Trigger file upload with the first image/video file
        changeFile(imageVideoFiles[0] as File);
        return;
      }
    }

    // Check for image data in clipboard (screenshots, copied images)
    const types = Array.from(clipboardData.types || []) as string[];
    const imageType = types.find((type: string) => type.startsWith('image/'));

    if (imageType) {
      // Get the image data as blob
      const itemsArray = Array.from(clipboardData.items || []) as DataTransferItem[];
      const imageItem = itemsArray.find((item: DataTransferItem) =>
        item.type.startsWith('image/')
      );

      if (imageItem && imageItem.getAsFile) {
        const blob = imageItem.getAsFile();
        if (blob) {
          // Create a File object from the blob
          const timestamp = new Date().getTime();
          const pastedFile = new File([blob], `pasted-image-${timestamp}.png`, { type: blob.type });

          // Trigger file upload
          changeFile(pastedFile);
          return;
        }
      }
    }

    // Check for HTML content that might contain images (block this)
    if (types.includes('text/html')) {
      const htmlContent = clipboardData.getData('text/html');
      if (htmlContent && htmlContent.includes('<img')) {
        // Block HTML with images
        return;
      }
    }

    // Get only plain text from clipboard
    const pastedText = clipboardData.getData('text/plain');

    // Insert only the plain text without any formatting
    if (pastedText && pastedText.trim()) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(pastedText);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // Fallback: replace entire content if no selection
        editableDivRef.current!.textContent = pastedText;
      }

      // Trigger input event to maintain consistency with typing
      const inputEvent = new Event('input', { bubbles: true });
      editableDivRef.current!.dispatchEvent(inputEvent);
    }
  }

  function handleKeydown(event?: ReactKeyboardEvent<HTMLDivElement>): void {
    // Two-step chip deletion (browser-owned chips). Backspace just after a chip
    // / Delete just before one: first press arms (highlights) the chip; second
    // press removes it. Any other key disarms (handled below / in onDivInput).
    if (event && (event.key === 'Backspace' || event.key === 'Delete')) {
      if ((event.nativeEvent as KeyboardEvent).isComposing) return; // IME
      const div = editableDivRef.current;
      if (div) {
        if (armedChipRef.current) {
          event.preventDefault();
          removeChip(armedChipRef.current);
          armedChipRef.current = null;
          setHasPrompt(!!div.querySelector('[data-chip]'));
          return;
        }
        const direction = event.key === 'Backspace' ? 'backward' : 'forward';
        const chip = chipAdjacentToCaret(div, direction);
        if (chip) {
          event.preventDefault();
          armChip(chip);
          armedChipRef.current = chip;
          return;
        }
      }
    } else if (event && !['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'].includes(event.key)) {
      // Any key other than a standalone modifier cancels an armed chip — this
      // includes Arrow/Home/End caret moves (spec: moving the caret cancels the
      // highlight) and any edit key.
      disarmCurrentChip();
    }
    if (event?.key === 'Enter') {
      // Prevent the default Enter behavior which is inserting a new line
      event.preventDefault();
    }
    setTimeout(() => {
      if (event) {
        if (event.key === 'Enter') {
          // Prevent the default Enter behavior which is inserting a new line
          event.preventDefault();
          if (isUrl(editableDivRef.current!.innerText)) {
            return;
          }
          if (useNavBarStore.getState().file) {
            uploadFile();
            return;
          }
          setSearchOrigin('free_text');
          prompterEnterPressed();
          return;
        }
      } else {
        if (isUrl(editableDivRef.current!.innerText)) {
          return;
        }
        // Prevent the default Enter behavior which is inserting a new line
        prompterEnterPressed();
        return;
      }
    });
  }

  function prompterEnterPressed(textFromUploadedImage?: string) {
    prompterEnterPressedOccurredRef.current = true;

    // Catalog Tier is a structured filter — it must not enter the free-text NLP
    // query (it stays visible in the bar and still rides the structured search).
    const text = textFromUploadedImage
      ? textFromUploadedImage
      : getQueryTextExcludingCatalogTier(editableDivRef.current!);

    if (useNavBarStore.getState().file) {
      uploadFile();
      prompterEnterPressedOccurredRef.current = false;
      return;
    }
    if (isUrl(text)) {
      prompterEnterPressedOccurredRef.current = false;
      return;
    }

    // Hide dropdown immediately when search is triggered
    closeSearchDropdownAndHideNothingFound();

    useGlobalStore.getState().setGlobalField('artistPages', -1);

    // Snapshot the sticky Catalog Tier scope BEFORE any reset. clearSearchBar
    // (when not on the home page) and startPrompterMagic's resetData both wipe
    // selectedFilters; since Catalog Tier is intentionally kept out of the
    // free-text query it is never echoed back as a tag, so we carry the snapshot
    // through and re-apply it after the search rebuilds the filters.
    const preservedCatalogTier = useFiltersStore.getState().getSelectedSubFilters('Catalog Tier');
    // Catalog Tier only exists for white-label users; only log when one is
    // actually present (the query itself is logged separately as the free-text
    // prompt breadcrumb, so non-white-label searches lose no information here).
    if (useUserStore.getState().isUserWhiteLabel && preservedCatalogTier.length > 0) {
      logger.info('search', 'catalog tier: captured before search', {
        preserved: preservedCatalogTier,
        querySentToNlp: text,
      });
    }

    // Freeze the bar on the user's typed text until the GSAP swap runs: while
    // pending, setSearchDivValue is suppressed so neither clearSearchBar's reset
    // nor the prompterText effect wipes or rewrites the bar before the animation.
    prompterSwapPendingRef.current = true;
    // searchWillFollow=true: the prompter search below navigates to the new
    // results URL, so skip the interim navigate('/') that bounced through home.
    clearSearchBar(getView() !== 'home', text, true);
    Promise.resolve().then(() => {
      if (getView() === 'home') {
        setShowNothingFound(false);
      }
      freeTextQuery(text)
        .then((prompterResult) => {
          prompterEnterPressedOccurredRef.current = false;
          useFiltersStore.getState().setSelectedArtwork(undefined);
          if (prompterResult.artworkNotAvailable) {
            // Extract the song search phrase to show in toast
            const songSearchTag = prompterResult.tags.find(tag =>
              !tag.filterId &&
              !tag.isArtistSearch &&
              tag.originalValueFromSearch
            );

            if (songSearchTag) {
              useToastStore.getState().info(
                `We couldn't find "${songSearchTag.originalValueFromSearch}" Log in or register to add it to our library!`
              );
              hideSearchDropdown(false, 'prompter:song-missing-toast');
            }
            // Don't return - continue processing filters
          }
          if (prompterResult.errorMessage) {
            useToastStore.getState().warning(prompterResult.errorMessage);
          }
          try {
            startPrompterMagic(prompterResult, preservedCatalogTier);
          } catch (err) {
            prompterSwapPendingRef.current = false; // release the freeze on failure
            sendPromptError(err);
            // NOTE(port): clearSearchBar no longer pre-navigates to home for a
            // search (searchWillFollow), so fall back to home when the search
            // flow fails before navigating to its results URL.
            navigate('/');
          }
        })
        .catch((err) => {
          prompterSwapPendingRef.current = false; // release the freeze on failure
          if (err.message.startsWith('HTTP error:')) {
            // This is specifically an HTTP error
            try {
              useToastStore.getState().warning('Oops, we can’t understand your query');
            } catch (toastErr) {
              console.log(toastErr);
            }
          } else {
            // Handle non-HTTP errors differently if necessary
            console.error('Processing error:', err);
          }
          // NOTE(port): clearSearchBar no longer pre-navigates to home for a
          // search (searchWillFollow); land on home when the query fails.
          navigate('/');
          // If we want error loges uncomment this.
          // sendPromptError(err)
        });
    });
  }

  function closeOverlay() {
    setOpenDropdown(null);
    lastFilterClickedRef.current = null;
  }

  function onDragOver(event: ReactDragEvent): void {
    event.preventDefault();
    setIsDragging(true);
  }

  function onDragLeave(event: ReactDragEvent): void {
    event.preventDefault();
    setIsDragging(false);
  }

  function onDrop(event: ReactDragEvent): void {
    event.preventDefault();
    setIsDragging(false);

    if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const files = event.dataTransfer?.files;
      if (files) {
        useNavBarStore.getState().resetFileState();
        changeFile(files[0]);
      }
    }
  }

  function uploadFile(): void {
    const navBarStore = useNavBarStore.getState();
    const currentFile = navBarStore.file;
    if (currentFile) {
      let uploadUrl = environment.baseUrlWithApi;
      if (navBarStore.isImageFile) {
        uploadUrl += 'free-text/find-by-image';
      } else if (navBarStore.isVideoFile) {
        uploadUrl += 'free-text/find-by-video';
      } else {
        if (navBarStore.isAudioFile) {
          const format = currentFile.name.substring(currentFile.name.lastIndexOf('.') + 1).toLowerCase();

          useToastStore.getState().warning(
            `${format.toUpperCase()} Format not supported. Type in song title and artist to find your song.`);
        } else {
          useToastStore.getState().error('This file is not supported');
        }
        resetFile();
        setSearchDivValue('');
        setContentEditable(true);
        setTimeout(() => {
          addPlaceholder();
        });
        return;
      }

      navBarStore.setNavBarField('uploadStarted', true);
      // A visual-search upload off the home view returns home WITHOUT navigating
      // (the drop zone/upload UI lives on home). Use the home override — cleared on
      // the next navigation (e.g. sendToMain, which pushes the results URL).
      if (getView() !== 'home') {
        useGlobalStore.getState().setViewOverride('home');
      }
      editableDivRef.current!.innerText = truncateFileName(currentFile.name);
      setHasPrompt(false);
      editableDivRef.current!.classList.add('placeholder');
      const formData = new FormData();
      formData.append('file', currentFile);

      uploadFileRequest(uploadUrl, formData, (event) => {
        // HttpEventType.UploadProgress
        if (event.total) {
          const progress = Math.round(100 * event.loaded / event.total);
          setUploadProgress(progress);
        }
      })
        .then((res) => {
          // HttpEventType.Response
          const response: any = res.data;

          const store = useNavBarStore.getState();
          store.setNavBarField('uploadStarted', false);
          store.setNavBarField('fileUploadComplete', true);

          setShowPromptCuratedPlaylists(false);

          store.setNavBarField('fileUploadResponse', [
            { key: response.data.reasoning1, value: response.data.description1 },
            { key: response.data.reasoning2, value: response.data.description2 },
            { key: response.data.reasoning3, value: response.data.description3 }
          ]);

          // complete:
          useNavBarStore.getState().setNavBarField('file', null);
        })
        .catch((err: any) => {
          console.error('File upload failed: ', err);
          const store = useNavBarStore.getState();
          store.setNavBarField('uploadFailed', true);
          store.setNavBarField('uploadStarted', false);
          // Angular: err.error.error.message || err.message (err.error = response body)
          store.setNavBarField('uploadError', err.response?.data?.error?.message || err.message);

          store.setNavBarField('imagePreview', undefined);
          store.setNavBarField('videoPreview', undefined);
          store.setNavBarField('fileUploadComplete', false);
          setUploadProgress(0);
          store.setNavBarField('fileUploadResponse', undefined);
          store.setNavBarField('file', null);
          setSearchDivValue('');
          setContentEditable(true);
          setTimeout(() => {
            addPlaceholder();
          });

          setShowPromptCuratedPlaylists(true);
        });
      return;
    }
  }

  function sendToMain(shortPrompt: string | undefined) {
    const navBarStore = useNavBarStore.getState();
    if (navBarStore.isImageFile) {
      setSearchOrigin('photo_upload');
    }
    if (navBarStore.isVideoFile) {
      setSearchOrigin('video_upload');
    }

    navBarStore.setNavBarField('file', null);
    useFiltersStore.getState().setPrompterText(shortPrompt);
    setPromptFromNewHomeScreen(shortPrompt);
    // Visual-search "send to main" shows results without navigating; the override
    // (cleared on the next navigation) bridges until/unless a search navigates.
    useGlobalStore.getState().setViewOverride('results');
  }

  function promptClicked(songhuntLink: string) {
    setSearchOrigin('playlist_clicked');
    closeSearchDropdownAndHideNothingFound();
    setSearchDivValue('');
    setContentEditable(true);
    addPlaceholder();

    songhuntLink = songhuntLink.replace(/%20/g, ' ');
    songhuntLink = songhuntLink.replace(/%22/g, '"');
    const queryString = songhuntLink.split('?')[1];
    // Split the query string into individual parameters
    const queryParams = queryString.split('&').reduce((acc, part) => {
      const [key, value] = part.split('=');
      acc[key] = value;
      return acc;
    }, {} as any);

    setShowPromptCuratedPlaylists(false);

    // Angular: this.router.navigate([''], {queryParams}).then();
    const search = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    navigate('/', { search });
  }

  function closeSearchDropdownAndHideNothingFound() {
    setShowNothingFound(false);
    hideSearchDropdown(true, 'close-and-hide-nothing-found');
  }

  function showPlaylist() {
    closeOverlay();
    useGlobalStore.getState().setGlobalField('showPlaylist', true);
  }

  function goBackToHomePage() {
    // NOTE(port): reload to the app's own base path (`/songhunt/` in prod, `/`
    // in dev) for the intentional go-home hard reset. The previous hardcoded
    // production URL navigated dev sessions away from localhost.
    window.location.href = import.meta.env.BASE_URL;
  }

  function showSignUpPopup() {
    if (!useUserStore.getState().user) {
      openUpgradeLogin('Sign Up for FREE for unlimited access to Songhunt', false, true);
    }
  }

  function searchIconEvent() {
    setSearchOrigin('free_text');
    prompterEnterPressed();
  }

  function isIphone() {
    return /iPhone/.test(navigator.userAgent) && !(window as any).MSStream;
  }

  function changeFile(newFile?: File) {
    const changeFileHappened = useNavBarStore.getState()
      .changeFile(useDeviceStore.getState().isMobileDevice, newFile);
    if (changeFileHappened) {
      editableDivRef.current!.innerText = truncateFileName(newFile!.name);
      setHasPrompt(false);
      setContentEditable(false);
      prompterEnterPressed();
    }
  }

  // ---------------------------------------------------------------------------
  // Private methods (Angular `private` members)
  // ---------------------------------------------------------------------------

  function openUpgradeLogin(loginPopupTitle: string, onlyExplorer: boolean, hideCloseButton: boolean = false): void {
    useModalStore.getState().open(ModalType.LOGIN_REGISTER, {
      inputs: {
        loginPopupTitle,
        onlyExplorer,
        hideCloseButton
      }
    });
  }

  function resetAllToStart(text = '') {
    const globalStore = useGlobalStore.getState();
    globalStore.setGlobalField('artistPages', -1);
    globalStore.setGlobalField('totalTimesNoSongsFound', 0);
    // this is done manually to avoid glitching
    setTimeout(() => {
      if (navbarRef.current?.classList.contains('big-search-home-page')) {
        navbarRef.current.style.background = 'transparent';
      }
    }, 300);
    closeSearchDropdownAndHideNothingFound();

    setErrorMessageFromPrompt(undefined);
    globalStore.setGlobalField('highlightedSong', undefined);
    usePlayerStore.setState({ selectedSong: undefined }); // selectedSongSignal.set(undefined)

    globalStore.setSongs([]);
    // Results just got cleared for a new search — drop the navigation dedup guard
    // so an explicit search that resolves to the same filters as the previous one
    // still navigates and refetches (otherwise the cleared list is never refilled).
    resetNavigateDedup();
    setTimeout(() => {
      useGlobalStore.getState().setSongsLoading(false);
    }, 0);

    if (useFiltersStore.getState().filtersVisible) {
      toggleFilters();
    }
    globalStore.setGlobalField('showNoMoreResults', false);
    globalStore.setGlobalField('artistPages', -1);

    if (!clearSearchClickedRef.current || getView() === 'home') {
      setShowPromptCuratedPlaylists(true);
    }
    if (!useNavBarStore.getState().uploadStarted) {
      if (useNavBarStore.getState().fileUploadResponse) {
        setTimeout(() => {
          editableDivRef.current!.innerText = truncateFileName(useNavBarStore.getState().file?.name);
          setHasPrompt(false);
          editableDivRef.current!.classList.add('placeholder');
          setContentEditable(false);
        }, 1000);
      } else {
        if (text === '') {
          clearEditableDivToEmpty();
          setContentEditable(true);
        }
        setTimeout(() => {
          if (editableDivRef.current && !editableDivRef.current.innerText.trim() &&
            !editableDivRef.current.innerHTML.trim()) {
            addPlaceholder();
          }
        }, 0);
      }
    }
    setIsTextInsideSearch(false);
  }

  function truncateFileName(fileName?: string, maxLength: number = 15): string {
    if (!fileName) {
      return currentFileNameRef.current;
    }
    currentFileNameRef.current = truncateFileNameHelper(fileName, maxLength);
    return currentFileNameRef.current;
  }

  function setContentEditable(isEditable: boolean): void {
    if (editableDivRef.current) {
      setContentEditableHelper(editableDivRef.current, isEditable);
    }
  }

  function hideSearchDropdown(hide: boolean, origin = 'unknown') {
    const wasHidden = useNavBarStore.getState().searchDropDownHidden;
    useNavBarStore.getState().setSearchDropDownHidden(hide);
    // Log only the actual open/close transition (not every defensive re-call),
    // tagged with who triggered it, so a downloaded log shows the autocomplete
    // dropdown's lifecycle and exactly what opened or closed it.
    if (wasHidden !== hide) {
      logger.info('app', `search dropdown ${hide ? 'closed' : 'opened'}`, { origin });
    }
    if (!hide) {
      setTimeout(() => {
        if (searchDropdownRef.current) {
          searchDropdownRef.current.scrollTo(0, 0);
        }
      }, 100);
    }
  }

  function getSongs(searchStr: string | null) {
    if (prompterEnterPressedOccurredRef.current) {
      return;
    }
    // version with Frontend prompter
    if (searchStr) {
      currentSearchStrRef.current = searchStr;
      fetchSongsByQuery(searchStr)
        .then((foundSongs) => {
          if (foundSongs.length === 0) {
            setPrompterIsActive(true);
            hideSearchDropdown(true, 'get-songs:no-results');
            checkInput(searchStr);
            sendEventToServer('SONG_MISSING', searchStr);
          }
          setArtworks(foundSongs);
        });
    } else {
      setArtworks([]);
    }
  }

  // preservedCatalogTier is captured by the caller BEFORE any reset (clearSearchBar
  // and resetData below both wipe selectedFilters). Catalog Tier is intentionally
  // kept out of the free-text query so the backend never echoes it back as a tag —
  // we re-apply the caller's snapshot after the tag rebuild so the search keeps it.
  function startPrompterMagic(
    prompterResult: PrompterResult,
    preservedCatalogTier: { id: number; state: 'green' | 'red' }[] = []
  ) {
    const filtersStore = useFiltersStore.getState();
    resetData();
    let artistSearchStr: string | undefined;
    let multipleArtworksIds: string | undefined;
    let searchOnThisArtist: string | undefined;

    if (prompterResult.sortById) {
      const sortBy = filtersStore.getSortById(prompterResult.sortById);
      filtersStore.changeSelectedSort(sortBy);
    }

    setErrorMessageFromPrompt(prompterResult.errorMessage);
    if (prompterResult.errorMessage) {
      setShowNothingFound(true);
      useToastStore.getState().warning(prompterResult.errorMessage);
    }

    if (prompterResult.lyricsContains) {
      filtersStore.setLyricsContains(prompterResult.lyricsContains);
    }

    prompterResult.tags.map((tag) => {
      if (tag.genresOperator) {
        useFiltersStore.getState().tagsWithOperator.push(tag);
      }
      if (tag.filterId) {
        const filterFromDictionary = useFiltersStore.getState().filtersDictionaryForPrompter[+tag.filterId!];
        if (!filterFromDictionary) {
          return;
        }
        if (tag.originalValueFromSearch || tag.originalValueFromSearch === '') {
          tag.replacedWith = filterFromDictionary.name.replace(/.*?:\s*/, '');
          tag.filterFamilyName = filterFromDictionary.familyName;
        }
        const filter = useFiltersStore.getState().filters.find((f) => f.title === filterFromDictionary.familyName)!;
        const subFilter = filter.filters.find((f) => f.id === +tag.filterId!)!;
        useFiltersStore.getState().addOrChangeSubFilterSelectedFilters(
          filter, subFilter, tag.state ? 'green' : 'red', false, tag.genresOperator);
      }

      if (tag.selectedArtwork) {
        tag.replacedWith = `${tag.selectedArtwork.artist} - ${tag.selectedArtwork.title}`;
        manuallySelectedArtworkRef.current = true;
        useFiltersStore.getState().changeSelectedArtwork(tag.selectedArtwork);
      }

      if (tag.isArtistSearch) {
        artistSearchStr = tag.replacedWith;
      }

      if (tag.multipleArtworksIds) {
        multipleArtworksIds = tag.multipleArtworksIds;
        searchOnThisArtist = tag.replacedWith;
      }
    });

    // Re-apply the preserved Catalog Tier onto the rebuilt filter tree before the
    // payload/URL is constructed below, so the search runs against it.
    useFiltersStore.getState().reapplySubFilters('Catalog Tier', preservedCatalogTier);
    const nowSelectedCatalogTier = useFiltersStore.getState().getSelectedSubFilters('Catalog Tier');
    // Only a white-label user with an actual Catalog Tier reaches this — keep the
    // log out of normal users' diagnostics where it was an empty-array no-op.
    if (useUserStore.getState().isUserWhiteLabel &&
        (preservedCatalogTier.length > 0 || nowSelectedCatalogTier.length > 0)) {
      logger.info('search', 'catalog tier: re-applied after prompter rebuild', {
        reapplied: preservedCatalogTier,
        nowSelected: nowSelectedCatalogTier,
      });
    }

    useFiltersStore.getState().setArtistSearchStr(artistSearchStr);
    useFiltersStore.getState().setMultipleArtworksIds(multipleArtworksIds);
    useFiltersStore.getState().setSearchOnThisArtist(searchOnThisArtist);
    useFiltersStore.getState().setNarrative(prompterResult.narrative);
    useFiltersStore.getState().setRandomUpdateSearchPayloadSignal();

    constructFiltersAndNavigate();

    finishPrompterMagic(prompterResult);
  }

  function filtersClickedEvent() {
    useGlobalStore.getState().resetPageNumber();
    constructFiltersAndNavigate();
  }

  function getSongsFamilies(referenceSongIds: string, songIds: number[]) {
    getResultsOsFamilies(referenceSongIds, songIds)
      .then((finalSongs) => {
        useGlobalStore.getState().setSongsLyricsMatch(finalSongs);
      }).finally(() => {
        useFiltersStore.getState().setTagsWithOperator([]);
      });
  }

  function getNoMoreResultsMessage(artistNotFound = false) {
    const globalStore = useGlobalStore.getState();
    const filtersStore = useFiltersStore.getState();
    const selectedSort = filtersStore.getSelectedSortBy();

    // Pure decision delegated to nav-bar-logic.noMoreResultsMessageFor.
    const message = noMoreResultsMessageFor(
      artistNotFound,
      globalStore.songs.length > 0,
      filtersStore.areFiltersEmpty(),
      !!filtersStore.selectedArtwork,
      selectedSort?.name
    );

    if (message === '__skip__') {
      return;
    }
    if (message === null) {
      // Default branch (filters present, or no reference artwork) — matches the
      // original code's final getDefaultNoMoreResultsMessage() fallthrough.
      globalStore.setGlobalField('noMoreResultsMessage', getDefaultNoMoreResultsMessage());
      return;
    }
    globalStore.setGlobalField('noMoreResultsMessage', message);
  }

  function setSearchDivValue(value: string | undefined, isPrompterActive = false) {
    if (editableDivRef.current && !isPrompterActive && !useNavBarStore.getState().file
        && !prompterSwapPendingRef.current) {
      removePlaceholder();
      if (value || value === '') {
        editableDivRef.current.innerText = value;
      } else {
        const html = useFiltersStore.getState().prompterText ?? '';
        renderPromptIntoDiv(html);
      }
    }
  }

  function disarmCurrentChip() {
    disarmChip(armedChipRef.current);
    armedChipRef.current = null;
  }

  // Delegated chip click (replaces <PromptTag onClick>). The chip carries its
  // semantics as DOM attributes; we dispatch off those. The chip element is the
  // floating-ui anchor for the filter dropdown.
  function onDivClick(e: ReactMouseEvent<HTMLDivElement>) {
    disarmCurrentChip();
    const chip = (e.target as HTMLElement).closest<HTMLElement>('[data-chip]');
    if (!chip) return;
    e.stopPropagation();
    if (chip.classList.contains('input-artwork')) { inputArtworkAction(); return; }
    if (chip.hasAttribute('artist-name')) {
      inputArtistActionByName(chip.getAttribute('artist-name') ?? '');
      return;
    }
    const familyName = chip.getAttribute('filter-family-name');
    if (!familyName) return; // deep-link "Songs similar to" title chip is non-interactive
    inputFilterActionByName(familyName, chip);
  }

  function inputFilterActionByName(filterFamilyName: string, anchor: HTMLElement) {
    const foundFilter = useFiltersStore.getState().filters.find((filter) => filter.title === filterFamilyName)!;
    if (!useUserStore.getState().user && useFiltersStore.getState().lockedFilters.includes(filterFamilyName)) {
      showSignUpPopup();
      return;
    }
    if (filterFamilyName === 'Metadata') {
      openFilterDropdown(anchor, foundFilter, true, true);
    } else {
      openFilterDropdown(anchor, foundFilter, false, true);
    }
  }

  function inputArtworkAction() {
    getSongs(useFiltersStore.getState().selectedArtwork!.artist);
    hideSearchDropdown(false, 'input-artwork-action');
  }

  function inputArtistActionByName(artistName: string) {
    const searchValue = useFiltersStore.getState().selectedArtwork?.artist || artistName;
    getSongs(searchValue);
    hideSearchDropdown(false, 'input-artist-action');
  }

  function resetPrompter() {
    useFiltersStore.getState().setPrompterText(undefined);
    useFiltersStore.getState().resetParams();
    setPrompterIsActive(false);
    setIsTextInsideSearch(false);

    exactMatchToastShownRef.current = false;
  }

  function changeArtworkInPrompter(artwork: Artwork) {
    // changeSelectedArtwork regenerates prompterText, which fires the prompterText
    // effect and re-renders the chips as React-owned JSX (replaces the old
    // span-surgery that rewrote the chip's class/attributes/text imperatively).
    useFiltersStore.getState().changeSelectedArtwork(artwork);
    hideSearchDropdown(true, 'change-artwork-in-prompter');
    constructFiltersAndNavigate();
  }

  function startFinalSongsFlow(finalSongs: FinalSong[], isLoadMore: boolean, selectedId: string) {
    if (finalSongs.length === 0) {
      useGlobalStore.getState().setGlobalField('totalTimesNoSongsFound',
        useGlobalStore.getState().totalTimesNoSongsFound + 1);
    } else {
      checkExactArtistMatch(finalSongs[0]);
    }

    const noMoreResults = finalSongs.length === 0 && !isLoadMore;
    if (noMoreResults) {
      getNoMoreResultsMessage();
    }

    // Check if needs to enter emptySong

    const isInArtistSearch = location.href.includes('searchInArtist=') || location.href.includes('/artist/');

    if (finalSongs.length < 20 && isInArtistSearch && useGlobalStore.getState().songs.length !== 0) {
      const emptySong: FinalSong = {
        artist: '',
        id: '',
        imageUrl: '',
        large_image_url: '',
        songAddedToPlaylist: false,
        title: '',
        url: '',
        webp_url: '',
        youtube_id: '',
        showEmptyLine: true,
        timestamp: 0
      };
      finalSongs.push(emptySong);

      useGlobalStore.getState().setGlobalField('artistPages', useGlobalStore.getState().resultsPageNumber);
      loadMore();
    } else {
      if (finalSongs.length < 25) {
        if (useGlobalStore.getState().totalTimesNoSongsFound > 3) {
          if (finalSongs.length === 0) {
            const totalSongs = useGlobalStore.getState().songs.length;
            getNoMoreResultsMessage(totalSongs === 0);
            useGlobalStore.getState().setGlobalField('showNoMoreResults', totalSongs === 0);
          }
          return;
        }
        loadMore();
      }
    }

    useGlobalStore.getState().setSongs(finalSongs);
    useGlobalStore.getState().setSongsLoading(false);
    if (!getIsQuickTags() && !noMoreResults) {
      useGlobalStore.getState().setGlobalField('highlightedSong', finalSongs[0]);
      getSongsFamilies(selectedId, finalSongs.map((song) => song.id).map(Number));
    }
  }

  function filtersWereClicked() {
    setSearchOrigin('filter_click');

    cancelRequests(); // Cancel any ongoing request
    filtersClickedEvent();
    closeOverlay();
    useGlobalStore.getState().setGlobalField('filterWasClickedDontHideFilters', true);
    // Re-render the prompt chips from the (regenerated) prompterText as React JSX.
    renderPromptIntoDiv(useFiltersStore.getState().prompterText ?? '');
  }

  function sendPromptError(err: any) {
    const errorObject = {
      prompt: editableDivRef.current!.innerText,
      err
    };
    sendEventToServer('QUERY_ERROR', errorObject);
  }

  function setDivSearchValueFromUrl(value: string) {
    document.title = value;
    removePlaceholder();
    // Mode A→B: render the deep-link title as imperative chips. The empty
    // familyName makes the title chip non-interactive via the onDivClick
    // guard, matching the original (that chip had no click handler).
    renderSegmentsIntoDiv([
      { kind: 'text', text: 'Songs similar to ' },
      { kind: 'filter', label: value, familyName: '' },
    ]);
  }

  function isUrl(input: string): boolean {
    const isUrlResult = isUrlHelper(input);

    if (isUrlResult) {
      setShowNothingFound(true);
      setShowLinkHasBeenEntered(true);
      hideSearchDropdown(false, 'is-url:link-entered');
    } else {
      setShowNothingFound(false);
      setShowLinkHasBeenEntered(false);
      hideSearchDropdown(false, 'is-url:not-a-link');
    }

    return isUrlResult;
  }

  function checkInput(input: string): void {
    const shouldShowNothingFound = containsForbiddenWords(input);

    if (shouldShowNothingFound) {
      setShowNothingFound(true);
      hideSearchDropdown(false, 'check-input:forbidden-words');
    } else {
      setShowNothingFound(false);
    }
  }

  function checkRequestsForPopup() {
    if (!useUserStore.getState().user) {
      const currentLocation = location.href;

      if (currentLocation !== activeUrlLocationRef.current) {
        activeUrlLocationRef.current = currentLocation;

        if (currentRequestsBeforePopupRef.current++ >= TOTAL_REQUESTS_BEFORE_POPUP) {
          openUpgradeLogin('Sign Up for FREE for unlimited access to Songhunt', false, true);
          return true;
        }
      }
    }
    return false;
  }

  function checkIfIsHomePage() {
    // Get the origin and href
    const origin = window.location.origin;
    const href = window.location.href;

    // Add a trailing slash to the origin if it doesn't already have one
    const normalizedOrigin = origin.endsWith('/') ? origin : origin + '/';

    // Compare the normalized origin with the href
    if (href !== normalizedOrigin && href.startsWith(normalizedOrigin)) {
      // The view now derives from the URL — no flag to flip here. Keep the
      // checkUtmTags() side-effect (it populates utmTags, used for Overwolf
      // detection) and the prompt-carousel reset for non-UTM URLs.
      if (!useUserStore.getState().checkUtmTags()) {
        setShowPromptCuratedPlaylists(false);
      }
    } else {
      // NOTE(port): Angular tested `!this.navBarService.uploadStarted` on the
      // signal REFERENCE (always truthy → condition always false) — the
      // "reset to big search home page + clear file" branch never executed.
      // Dead branch preserved as a no-op.
    }
    // NOTE(port): same signal-reference bug — `setContentEditable(true)` was
    // never reached here in Angular. Preserved as a no-op.
    clearSearchClickedRef.current = false;
  }

  function rollBigScreenPrompts(playlists: CuratedPlaylist[]) {
    // Keep only playlists that have both a caption and an image.
    // `!= null` (not `!== null`) so a missing/undefined shortPrompt is also
    // filtered out — the field is optional (string | undefined).
    playlists = playlists
      .filter(playlist => playlist.shortPrompt != null && playlist.promptImageUrl != null);

    // Show ALL of them in the carousel, shuffled once so the order feels fresh.
    setCuratedPromptPlaylists(shuffle(playlists));
  }

  function resetFile() {
    useNavBarStore.getState().setNavBarField('uploadStarted', false);
    useNavBarStore.getState().setNavBarField('uploadFailed', false);
    setUploadProgress(0);
    // NOTE(port): Angular's `if (!this.fileUploadResponse() && !this.navBarService.uploadStarted)`
    // tested the uploadStarted signal REFERENCE (always truthy → condition
    // always false) — clearing fileUploadResponse/file never executed.
    // Dead branch preserved as a no-op.
  }

  function checkIfWhiteLabelDomainAndReset() {
    const domain = location.host.split('.')[0];
    const isWhiteLabel = domain !== 'www' && !domain.includes('localhost:');
    if (isWhiteLabel) {
      getResults();
    }
  }

  function checkExactArtistMatch(finalSong: FinalSong) {
    const filtersStore = useFiltersStore.getState();
    if (!useFiltersStore.getState().prompterText?.toString().includes('Songs similar to') && !exactMatchToastShownRef.current) {
      const artistName =
        useHttpReqStore.getState().artistFromUrl
        || filtersStore.artistSearchStr
        || filtersStore.searchOnThisArtist;
      const isEmpty = Object.keys(filtersStore.selectedFilters).length === 0;
      let artistFromSongList = '';
      if (useGlobalStore.getState().songs.length > 0) {
        artistFromSongList = useGlobalStore.getState().songs[0].artist.toLowerCase();
      } else {
        artistFromSongList = finalSong.artist.toLowerCase();
      }

      if (isEmpty && artistName && artistName.toLowerCase() !== artistFromSongList) {
        exactMatchToastShownRef.current = true;
        useToastStore.getState().warning('We couldn\'t find a perfect match. Try tweaking your search.');
      }
    }
  }

  function checkAndAddTopSongs() {
    let shouldAddSongs = false;
    let origin: string = '';

    if (addTopTwentySongsToPlaylistFromPromptRef.current) {
      addTopTwentySongsToPlaylistFromPromptRef.current = false;
      origin = 'SH_TOP_20_ADD_PROMPT';
      shouldAddSongs = true;
    } else if (!addedTopTwentySongsToPlaylistFromFirstSearchRef.current) {
      origin = 'SH_TOP_20_ADD_FIRST_SEARCH';
      addedTopTwentySongsToPlaylistFromFirstSearchRef.current = true;
      shouldAddSongs = true;
    }

    if (shouldAddSongs && !exactMatchToastShownRef.current) {
      addTopTwentySongsToPlaylist(origin);
      useToastStore.getState().info(
        `We added the first ${useGlobalStore.getState().playlistSongs.size} songs to your playlist. Export it to Spotify or keep exploring your song recommendations!`,
        8000);
      showPlaylist();
      if (useDeviceStore.getState().isMobileDevice) {
        setTimeout(() => {
          useGlobalStore.getState().setGlobalField('showPlaylist', false);
        }, 2000);
      }
    }
  }

  function finishPrompterMagic(prompterResult: PrompterResult) {
    setTimeout(() => {
      const currentPrompterText = useFiltersStore.getState().prompterText;
      if (currentPrompterText) {
        tempPrompterResultRef.current = undefined;
        prompterResult.newTextForSearch = htmlToPlainText(currentPrompterText);

        if (prompterResult.addTopTwentyToPlaylist) {
          addTopTwentySongsToPlaylistFromPromptRef.current = true;
        }

        // Pulse & Assemble: glow the bar, pop the typed words out, swap in
        // the canonical tag chips, spring the new spans in (see
        // prompter-transition.ts).
        const barEl =
          (editableDivRef.current!.closest('.search-container') as HTMLElement | null)
          ?? editableDivRef.current!;
        prompterTransition.start(
          editableDivRef.current!,
          barEl,
          // onSwap: clear the imperative word-spans, then inject the canonical
          // tag chips as browser-owned HTML. Runs exactly once per start()
          // (incl. the reduced-motion and stop()-flush paths).
          () => {
            // The swap is the A→B handoff: release the freeze, then render the
            // chips (the transition immediately springs the new spans in).
            prompterSwapPendingRef.current = false;
            renderPromptIntoDiv(currentPrompterText);
          }
        );
      } else {
        tempPrompterResultRef.current = prompterResult;
      }
    }, 500);
  }

  // Router NavigationEnd equivalent (Angular: router.events NavigationEnd
  // subscription in ngOnInit). Runs once for the initial URL on mount (init
  // effect) and on every client-side route change (routeKey effect below).
  function handleNavigationEnd() {
    useGlobalStore.getState().resetPageNumber();
    // NOTE(port): clear the in-flight prompter guard on every navigation so a
    // prior Enter-press (e.g. before a back/forward nav) can't leave getSongs()
    // permanently short-circuited and silently kill the autocomplete dropdown.
    prompterEnterPressedOccurredRef.current = false;
    checkIfIsHomePage();
    // Angular used event.urlAfterRedirects (router URL, e.g. '/artist?x=1') —
    // reconstructed from pathname + search here.
    const routerUrl = window.location.pathname + window.location.search;
    const pathParts = routerUrl.split('/')[1];
    // NOTE(port): home-vs-results no longer toggles a flag here — selectView
    // derives 'results' from real search params (id_*, referenceSongIds; UTM-only
    // stays home) and 'home' from a clean URL (the visual-search soft-home shows
    // its uploaded media + 3 prompts via fileUploadResponse on the home view).
    useGlobalStore.getState().setGlobalField('logoLocationLink', location.origin + '/' + pathParts);
  }

  // ===========================================================================
  // Effects. Declaration order mirrors Angular execution order on first CD:
  // ngOnInit → prompterText$ subscription (initial emission) → addPlaceholder
  // (both ngAfterViewInit) → constructor effect()s (flushed after first CD).
  // ===========================================================================

  // inputEvents Subject: debounce; distinct/tap/subscribe run in
  // handleDebouncedInput. The ref trampoline keeps the debounced identity
  // stable across renders while always invoking the latest closure.
  // NOTE(port): Angular used debounceTime(500); lowered to 250ms here for a
  // snappier type-ahead (results appear sooner after the user stops typing).
  // Repeat/backspaced queries are served from cache (see fetchSongsByQuery), so
  // the extra fires from the shorter window mostly hit cache, not the network.
  const inputSubscriberRef = useRef<(value: string) => void>(handleDebouncedInput);
  inputSubscriberRef.current = handleDebouncedInput;
  const inputEventsNext = useMemo(
    () => debounce((value: string) => inputSubscriberRef.current(value), 250),
    []
  );

  // Same trampoline for the dropdowns' filtersClicked.pipe(debounceTime(150)).
  const filtersWereClickedRef = useRef<() => void>(filtersWereClicked);
  filtersWereClickedRef.current = filtersWereClicked;
  const debouncedFiltersClicked = useMemo(
    () => debounce(() => filtersWereClickedRef.current(), 150),
    []
  );

  // --- ngOnInit + ngAfterViewInit (StrictMode-guarded; the window listeners
  // were never removed in Angular either — they live for the app lifetime) ---
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) {
      return;
    }
    didInitRef.current = true;

    window.addEventListener('popstate', (event: any) => {
      const bareRootHref = location.origin + '/';
      const appHomeHref = location.origin + import.meta.env.BASE_URL;
      const eventHref = event?.target?.location?.href;
      const currentUrl = window.location.href;

      // NOTE(port): going to the app's own home is an intentional hard reset (the
      // logo does the same via goBackToHomePage) — reload to BASE_URL, which
      // wipes all in-memory state. The previous `'/'` dropped the `/songhunt/`
      // prefix and bounced the user out to the bare domain root.
      //
      // Bug 1 EXCEPTION: when a visual-search session is still in memory (an
      // uploaded image/video + its 3 prompts), return home SOFTLY (no reload) so
      // it survives and the user can pick another prompt without re-uploading.
      // Mirrors the Angular app, which kept these as in-memory signals across the
      // same soft home transition (resetAllToStart preserves fileUploadResponse).
      const action = decidePopstateAction(
        eventHref,
        appHomeHref,
        bareRootHref,
        !!useNavBarStore.getState().fileUploadResponse,
      );
      if (action === 'reload-base') {
        window.location.href = import.meta.env.BASE_URL;
      } else if (action === 'reload-root') {
        // Back to the bare domain root — leaving the app entirely.
        window.location.href = '/';
      }
      // 'show-home-soft' / 'sync-from-url': no flags to set — react-router has
      // already updated the location, so the derived view follows the popped URL
      // (home for the clean app URL, curated for a top-playlists URL, etc.).
    });
    window.addEventListener('resize', () => {
      // timeout is needed to initialize el element
      setTimeout(() => {
        onFiltersScroll();
      }, 100);
    }, true);

    // Router NavigationEnd subscription → initial run (see handleNavigationEnd).
    handleNavigationEnd();

    const currentUrl = window.location.href;

    // (top-playlists no longer sets flags here — the view derives 'curated' from
    // the URL via isCuratedPath.)

    if (currentUrl.includes('/similar/')) {
      const searchStr = extractLastSegment(currentUrl).replaceAll('-', ' ');
      fetchSongsByQuery(searchStr)
        .then((foundArtworks) => {
          if (foundArtworks.length === 0) {
            sendEventToServer('SONG_MISSING', searchStr);
          }
          removePlaceholder();
          artworkFromUrlRef.current = foundArtworks[0];
          setDivSearchValueFromUrl(artworkFromUrlRef.current.artist + ' - ' + artworkFromUrlRef.current.title);

          setIsQuickTags(false);
          useHttpReqStore.getState().setSongFromUrl(artworkFromUrlRef.current.id.toString());
        });
    }

    if (currentUrl.includes('/artist/')) {
      const artist = extractLastSegment(currentUrl).replaceAll('-', ' ');
      freeTextQuery(artist)
        .then((prompterResult) => {
          Promise.resolve()
            .then(() => {
              const currentTag = prompterResult.tags.find((tag) => tag.isArtistSearch);
              useHttpReqStore.getState().setArtistFromUrl(currentTag?.replacedWith);
              if (prompterResult.errorMessage) {
                useToastStore.getState().warning(prompterResult.errorMessage);
                try {
                  startPrompterMagic(prompterResult);
                } catch (err) {
                  sendPromptError(err);
                }
                cancelRequests();
                getResults(false);
              }
            });
        });
    }

    // ngAfterViewInit: this.globalStateService.navBar = this.navbar;
    useGlobalStore.getState().setGlobalField('navBar', navbarRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Router NavigationEnd subscription: re-run on every client-side route
  // change (the initial URL was handled by the init effect above; skip the
  // mount run so it does not fire twice for the same navigation). ---
  const lastRouteKeyRef = useRef(routeKey);
  useEffect(() => {
    if (lastRouteKeyRef.current === routeKey) {
      return;
    }
    lastRouteKeyRef.current = routeKey;
    handleNavigationEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  // --- ngAfterViewInit: prompterText$ subscription (distinctUntilChanged is
  // inherent — the effect only re-runs when the store value changes; it also
  // runs on mount, replicating the BehaviorSubject's initial emission) ---
  useEffect(() => {
    setIsTextInsideSearch(!!(prompterText && prompterText !== ''));

    if (tempPrompterResultRef.current) {
      finishPrompterMagic(tempPrompterResultRef.current);
    }

    if (!prompterTransition.isAnimating()) {
      setSearchDivValue(undefined, getPrompterIsActive());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompterText]);

  // --- ngAfterViewInit: this.addPlaceholder(); ---
  const didAddInitialPlaceholderRef = useRef(false);
  useEffect(() => {
    if (didAddInitialPlaceholderRef.current) {
      return;
    }
    didAddInitialPlaceholderRef.current = true;
    addPlaceholder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- E1: combined [artistFromUrl, songFromUrl] + initialLoadingProcessComplete ---
  useEffect(() => {
    if (initialLoadingProcessComplete) {
      // When the signal is true, subscribe to the latest values
      // from artistFromUrl$ and songFromUrl$
      const searchInArtist = artistFromUrl;
      const referenceSongIds = songFromUrl;

      if (searchInArtist || referenceSongIds) {
        cancelRequests();
        getResults(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadingProcessComplete, artistFromUrl, songFromUrl]);

  // --- E2: react to home/results transitions (+ promptFromNewHomeScreen) ---
  useEffect(() => {
    // `show` is now the derived view (home vs not), already synchronous with the
    // URL — no `undefined` first-render state and no store-flag default-write. The
    // old else-branch (set the flag true when undefined) is obsolete: selectView
    // returns 'home' for a clean URL directly.
    const show = showBigSearchHomePage;

    if (show) {
      hideSearchDropdown(true, 'effect:big-search-home-page:show');
    }
    if (show && getPromptFromNewHomeScreen()) {
      return;
    }
    if (show !== currentShowBigSearchHomePageValueRef.current) {
      currentShowBigSearchHomePageValueRef.current = show;
    } else {
      return;
    }

    cancelRequests();
    useGlobalStore.getState().setSongs([]);
    stopGetResultsFinalFlowRef.current = true;

    hideSearchDropdown(true, 'effect:big-search-home-page');

    if (show) {
      resetAllToStart();
    } else {
      // This is done manually to avoid glitching. Clear the inline
      // `background: transparent` the home page set (NavBar resetAllToStart),
      // so the results nav falls back to .section-border's opaque background.
      // NOTE(port): the Angular effect guarded this on the nav still having
      // the `big-search-home-page` class. Under React's commit-then-effect
      // ordering the re-render strips that class before this effect runs, so
      // the guard always failed and the transparent style leaked onto the
      // results page. removeProperty is a harmless no-op when nothing is set.
      navbarRef.current?.style.removeProperty('background');
      setShowPromptCuratedPlaylists(false);
      resetFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBigSearchHomePage, promptFromNewHomeScreen]);

  // --- E3: loadMoreSongsEvent ---
  useEffect(() => {
    if (loadMoreSongsEvent) {
      loadMore();
      useGlobalStore.getState().setGlobalField('loadMoreSongsEvent', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMoreSongsEvent]);

  // --- E4: scrollEventOnMobile && filtersVisible ---
  useEffect(() => {
    if (scrollEventOnMobile && useFiltersStore.getState().filtersVisible) {
      toggleFilters();
      useGlobalStore.getState().setGlobalField('scrollEventOnMobile', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollEventOnMobile, filtersVisible]);

  // --- E5: curatedPlaylists → rollBigScreenPrompts ---
  useEffect(() => {
    if (curatedPlaylists && !getCuratedPromptPlaylists()) {
      rollBigScreenPrompts(curatedPlaylists);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curatedPlaylists, curatedPromptPlaylists]);

  // --- E6: promptFromNewHomeScreen → prompterEnterPressed ---
  useEffect(() => {
    const result = promptFromNewHomeScreen;
    if (result) {
      prompterEnterPressed(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptFromNewHomeScreen]);

  // --- E7: user → checkIfWhiteLabelDomainAndReset ---
  useEffect(() => {
    if (user) {
      checkIfWhiteLabelDomainAndReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // --- E8: referenceSongFromUrl + updateGlobalStateSignal ---
  useEffect(() => {
    if (referenceSongFromUrl && updateGlobalStateSignal) {
      if (referenceSongFromUrl > 0) {
        getArtwork(referenceSongFromUrl.toString())
          .then((artwork) => {
            manuallySelectedArtworkRef.current = true;
            useFiltersStore.getState().changeSelectedArtwork(artwork);
            setSearchDivValue(undefined, getPrompterIsActive());
            hideSearchDropdown(true, 'effect:reference-song-from-url');
            setIsQuickTags(false);
            useFiltersStore.getState().setFiltersVisible(!useDeviceStore.getState().isMobileDevice);
            // NOTE(port): the fetch runs once in .finally() below (covering both
            // the success and catch paths). The previous getResults() here fired
            // a second, redundant fetch on every reference-song navigation.
          })
          .catch(() => {
            useToastStore.getState().warning(`Sorry, we could not find this song, ID: ${referenceSongFromUrl.toString()}`);
            setIsQuickTags(true);
            clearSearchBar();
          })
          .finally(() => {
            getResults();
          });
        return;
      }

      if (referenceSongFromUrl === -1) {
        useFiltersStore.getState().setFiltersVisible(!useDeviceStore.getState().isMobileDevice);
        setTimeout(() => {
          onFiltersScroll();
        });
      }

      if (!useHttpReqStore.getState().songFromUrl) {
        getResults();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceSongFromUrl, updateGlobalStateSignal]);

  // --- E9: songs → checkAndAddTopSongs ---
  useEffect(() => {
    if (songs.length > 0) {
      checkAndAddTopSongs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songs]);

  // --- unmount: cancel pending debounces + kill prompter tweens ---
  useEffect(() => {
    return () => {
      inputEventsNext.cancel();
      debouncedFiltersClicked.cancel();
      prompterTransition.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // expandFadeIn :enter for the results filters panel (callback ref fires on
  // mount of the conditionally-rendered wrapper; Angular's :leave tween is not
  // ported — the element unmounts immediately, same as the rest of the port).
  const expandFadeInRef = useCallback((el: HTMLDivElement | null) => {
    if (el) {
      expandFadeInEnter(el);
    }
  }, []);

  // (outsideClick) on the <form> → outsideClickEvent().
  const formRef = useOutsideClick<HTMLFormElement>(() => outsideClickEvent());

  // ===========================================================================
  // Template (nav-bar.component.html — verbatim conversion)
  // ===========================================================================

  // NOTE(port): the clear/search-icon visibility was an Angular template
  // expression reading editableDiv.innerText each CD. It now derives from state
  // (no DOM read during render): typed text (isTextInsideSearch) OR a rendered
  // prompt (hasPrompt) OR a selected file.
  const hasSearchContent = isTextInsideSearch || hasPrompt || !!file;

  // ng-template #tpl (sort dropdown content of the [tp] popper).
  const sortTpl = (
    <div className="inline-block w-[250px] bg-zinc-100 dark:bg-zinc-800
              shadow-xl rounded-md overflow-hidden max-h-[250px]">
      <div
        className="px-3 py-2 bg-zinc-200 border-b border-zinc-400 rounded-t-lg dark:border-gray-600 dark:bg-zinc-700">
        <h3 className="font-semibold text-gray-900 dark:text-white">Sort songs by:</h3>
      </div>
      <ul className="h-48 px-3 pb-3 overflow-y-auto text-sm text-gray-700 dark:text-gray-200 overscroll-contain">
        {sortByArray.map((sortBy) => (
          <SortByItem key={sortBy.name} sortBy={sortBy} onSelect={selectSortBy} />
        ))}
      </ul>
    </div>
  );

  return (
    <>
      <Background isDragging={isDragging} showBigSearchHomePage={showBigSearchHomePage} />

      <nav ref={navbarRef}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        className={`navbar-root section-border fixed z-40 top-0 left-0 custom-box-shadow px-4 h-fit${showBigSearchHomePage ? ' big-search-home-page' : ''}${appTheme === 'dark' && showBigSearchHomePage ? ' dark-gradient' : ''}${appTheme === 'light' && showBigSearchHomePage ? ' light-gradient' : ''}`}>

        <div className="max-w-full flex flex-wrap items-center justify-between mx-auto pb-0">
          {/* Logo */}
          <Logo onGoBackToHomePage={goBackToHomePage}
            onOpenMenuDropdown={openMenuDropdown}
            appTheme={appTheme}
            showBigSearchHomePage={showBigSearchHomePage} />

          {/* Login / Playlist */}
          <div className={`flex items-baseline order-1 lg:-mt-[25px] lg:order-2 mb-0 ml-auto mr-2${showBigSearchHomePage ? ' !mt-0' : ''}`}>
            {/* Angular host: <mypart-playlist-icon class="-mt-[20px]"> */}
            <div className="-mt-[20px]">
              <PlaylistIcon onShowPlaylist={showPlaylist}
                totalPlaylistSongs={totalPlaylistSongs} />
            </div>
          </div>

          <div className={`flex items-baseline order-1 lg:-mt-[25px] lg:order-2 mb-0 mr-[-.5rem] md:mr-[0rem]${showBigSearchHomePage ? ' !mt-0' : ''}`}>
            {/* Login */}
            <LoginMenu onToggleDiscoveryMode={toggleDiscoveryMode}
              isPlaylistActive={totalPlaylistSongs > 0}
              className="-mt-[20px]" />
          </div>

          {/* Search Bar */}
          <div className={`search-bar-container${!showBigSearchHomePage ? ' middle-to-top' : ''}${showBigSearchHomePage ? ' top-to-middle lg:-ml-[7vw] xl:-ml-[2vw] lg:!w-3/4 sm:h-[60px] sm:!leading-[2.5]' : ''}`}>

            <form ref={formRef}
              onSubmit={(e) => e.preventDefault()}
              className="flex flex-col items-center w-full xl:w-[75%] relative">
              {showBigSearchHomePage && (
                <Subtitle />
              )}
              <label className="sr-only" htmlFor="editableDiv">Search</label>
              <div onClick={(e) => {
                e.stopPropagation();
                showSearchDropDown();
              }}
                className="relative w-full search-container">
                <div className="flex items-center pointer-events-none icon-container">
                  {isLoading ? (
                    <>
                      <svg aria-hidden="true"
                        className="w-4 h4 text-gray-200 animate-spin dark:text-gray-700 fill-gray-700 dark:fill-gray-200"
                        fill="none" viewBox="0 0 100 101" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="currentColor" />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentFill" />
                      </svg>
                      <span className="sr-only">Loading...</span>
                    </>
                  ) : (
                    <svg className={`w-4 h-4 text-gray-700 dark:text-gray-300${discoveryMode ? ' !text-mypart_red-500 dark:!text-mypart_red-500' : ''}`}
                      aria-hidden="true"
                      fill="none"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg">
                      <path d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2" />
                    </svg>
                  )}
                </div>

                <div ref={editableDivRef}
                  onBlur={() => addPlaceholder()}
                  onClick={onDivClick}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  onFocus={() => removePlaceholder()}
                  onInput={onDivInput}
                  onKeyDown={(e) => handleKeydown(e)}
                  onPaste={onPaste}
                  className="editable-div placeholder" contentEditable
                  suppressContentEditableWarning
                  enterKeyHint="search" id="editableDiv" spellCheck={false}>
                  {/* Browser-owned: the prompt is injected as HTML by
                      renderPromptIntoDiv; React renders no children here so it
                      never contends with the browser over these nodes. */}
                </div>

                <div className="flex flex-row gap-2 justify-center items-center">
                  {hasSearchContent && (
                    <>
                      <Tooltip content="Clear search">
                        <svg onClick={(e) => {
                          e.stopPropagation();
                          clearSearchText();
                        }}
                          aria-hidden="true"
                          className="cursor-pointer w-[14px] h-[14px] sm:w-[20px] sm:h-[20px] text-gray-800 dark:text-gray-300"
                          fill="none"
                          viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                          <path d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" stroke="currentColor" strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2" />
                        </svg>
                      </Tooltip>
                      <div className="h-8 mx-3 border-l border-gray-400 dark:border-gray-500"></div>
                    </>
                  )}

                  <FileUpload onChangeFile={changeFile}
                    onShowSignUpPopup={showSignUpPopup} />

                  {isTextInsideSearch && !isMobileDevice && (
                    <Tooltip disabled={isMobileDevice} content={file ? 'Upload file' : 'Search'}>
                      <svg onClick={searchIconEvent}
                        className={`w-6 h-6 text-gray-700 dark:text-gray-300 cursor-pointer ${PIXELATE_IN_OUT_ENTER_CLASS}${appTheme === 'light' ? ' light-mode' : ''}${appTheme === 'dark' ? ' dark-mode' : ''}`}
                        aria-hidden="true"
                        fill="none" height="24" viewBox="0 0 24 24"
                        width="24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path className="path-animation" d="M19 12H5m14 0-4 4m4-4-4-4" stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2" />
                      </svg>
                    </Tooltip>
                  )}

                  {/* Sorting */}
                  {!isQuickTags && (
                    <Popover open={sortOpen}
                      onOpenChange={setSortOpen}
                      popperClassName="custom-popper"
                      placement="bottom-end"
                      content={sortTpl}>
                      <div onClick={(e) => e.stopPropagation()}
                        className="relative w-[20px] h-[20px] sm:w-[24px] sm:h-[24px]">
                        <Tooltip disabled={isMobileDevice} content="Define similarity type">
                          <img alt={sortImgAlt}
                            className="block cursor-pointer z-30"
                            style={fillStyle}
                            src="https://d382nvfdu38z2f.cloudfront.net/songhunt/sort.webp" />
                        </Tooltip>
                      </div>
                    </Popover>
                  )}
                </div>
                {prompterIsActive && isMobileDevice && (
                  <div className="flex items-center
                      cursor-pointer icon-container">
                    <Tooltip disabled={isMobileDevice} content={file ? 'Upload file' : 'Search'}>
                      <svg onClick={() => prompterEnterPressed()}
                        className={`w-6 h-6 text-gray-700 dark:text-gray-300 cursor-pointer ${PIXELATE_IN_OUT_ENTER_CLASS}${appTheme === 'light' ? ' light-mode' : ''}${appTheme === 'dark' ? ' dark-mode' : ''}`}
                        aria-hidden="true"
                        fill="none" height="24" viewBox="0 0 24 24"
                        width="24"
                        xmlns="http://www.w3.org/2000/svg">
                        <path className="path-animation" d="M19 12H5m14 0-4 4m4-4-4-4" stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2" />
                      </svg>
                    </Tooltip>
                  </div>
                )}
              </div>

              <Search hidden={searchDropDownHidden}
                showBigSearchHomePage={showBigSearchHomePage}
                showNothingFound={showNothingFound}
                isLoading={isLoading}
                artworks={artworks}
                errorMessageFromPrompt={errorMessageFromPrompt}
                showLinkHasBeenEntered={showLinkHasBeenEntered}
                dropdownRef={searchDropdownRef}
                onSelectArtwork={selectArtwork}
                onSubmitSong={submitSong}
                onClose={closeSearchDropdownAndHideNothingFound} />

              {!showBigSearchHomePage && (
                <div className="w-[100vw] sm:w-full my-1 flex justify-between sm:justify-end items-center px-2 py-1.5 sm:p-1.5">
                  <div className="w-fit mr-auto ml-2 text-start">
                    <span onClick={() => addTopTwentySongsToPlaylist('SH_TOP_20_ADD')}
                      className="text-[.8rem] sm:text-sm font-bold text-black dark:text-white underline cursor-pointer">Add Top 20 to Playlist</span>
                  </div>
                  <SocialSharing link={getSearchLink()}
                    className="flex items-center -ml-[8px] w-[40px] h-full"
                    source="search" />

                  {!isIphone() && (
                    <Tooltip disabled={isMobileDevice} content={playAllSongs ? 'Stop Play All' : 'Play All Songs'}>
                      <svg onClick={(e) => {
                        e.stopPropagation();
                        togglePlayAllSongs();
                      }}
                        aria-hidden="true" className="text-mypart_red-500 mr-2 cursor-pointer h-4" fill="currentColor"
                        preserveAspectRatio="xMidYMid meet"
                        viewBox="0 0 358.000000 293.000000"
                        xmlns="http://www.w3.org/2000/svg">
                        <g fill="currentColor"
                          stroke="none" transform="translate(0.000000,293.000000) scale(0.100000,-0.100000)">
                          <path d="M81 2834 c-45 -37 -53 -79 -49 -246 3 -164 11 -186 79 -220 32 -17
                      120 -18 1419 -18 1323 0 1387 1 1419 18 64 34 72 65 69 255 l-3 169 -37 34
                      -38 34 -1414 0 -1414 0 -31 -26z" />
                          <path d="M85 1898 c-46 -36 -55 -73 -55 -230 0 -158 10 -193 63 -232 28 -21
                      36 -21 756 -21 678 0 729 1 754 18 51 33 61 60 65 188 6 175 -2 226 -45 266
                      l-34 33 -738 0 -738 0 -28 -22z" />
                          {playAllSongs ? (
                            <>
                              <rect height="1826" rx="70" ry="70" width="600" x="2100" y="0" />
                              <rect height="1826" rx="70" ry="70" width="600" x="2900" y="0" />
                            </>
                          ) : (
                            <path d="M2185 1826 c-40 -17 -84 -63 -101 -103 -11 -26 -14 -174 -14 -792 0
                      -512 3 -769 11 -788 24 -64 109 -122 179 -123 19 0 58 11 85 25 28 13 293 181
                      590 373 614 397 598 383 603 505 3 83 -17 130 -78 178 -50 39 -1108 719 -1137
                      730 -35 13 -103 10 -138 -5z" />
                          )}
                          <path d="M92 960 c-55 -34 -64 -71 -60 -249 5 -254 -22 -241 503 -241 437 0
                      437 0 475 63 18 28 20 51 20 190 0 176 -7 203 -63 237 -30 19 -53 20 -437 20
                      -389 0 -406 -1 -438 -20z" />
                        </g>
                      </svg>
                    </Tooltip>
                  )}
                  {!isMobileDevice && (
                    <div>
                      <span onClick={toggleFilters}
                        className="w-fit text-end text-[.8rem] sm:text-sm text-gray-500 dark:text-gray-400 mx-2 cursor-pointer select-none">
                        {isQuickTags
                          ? isMobileDevice ? 'Search with Filters' : 'Search with lyrical and musical filters'
                          : 'Refine search'}
                      </span>
                    </div>
                  )}

                  <button onClick={toggleFilters}
                    className={`rounded-full text-gray-900 bg-white border border-gray-300 focus:outline-none
                           hover:bg-gray-100 font-medium text-xs p-1.5
                           dark:bg-gray-700 dark:text-white dark:border-gray-500 dark:hover:bg-gray-700
                           dark:hover:border-gray-600${!filtersVisible && prompterText ? ' !bg-mypart_red-500' : ''}`}
                    type="button">
                    <svg className={`w-3 h-3 text-gray-800 dark:text-white arrow${filtersVisible ? ' rotate-180' : ''}${!filtersVisible && prompterText ? ' !text-white' : ''}`}
                      aria-hidden="true"
                      fill="none"
                      viewBox="0 0 14 8" xmlns="http://www.w3.org/2000/svg">
                      <path d="m1 1 5.326 5.7a.909.909 0 0 0 1.348 0L13 1" stroke="currentColor" strokeLinecap="round"
                        strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                    <span className="sr-only">Toggle filters</span>
                  </button>
                  {isMobileDevice && (
                    <div>
                      <span onClick={toggleFilters}
                        className="w-fit text-end text-[.8rem] sm:text-sm text-gray-500 dark:text-gray-400 mx-2 cursor-pointer select-none">
                        {isQuickTags
                          ? isMobileDevice ? 'Search with Filters' : 'Search with Lyrical and Musical Filters'
                          : 'Refine search'}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {showBigSearchHomePage && !(fileUploadComplete || uploadStarted || uploadFailed) && (
                <div className="absolute top-[50px] mt-8 md:mt-0 md:top-[72px] w-full">
                  <div ref={filtersScrollContainerRef}
                    className="filters-container w-full flex items-center justify-between whitespace-nowrap py-4 big-search-filters">
                    {filters.map((filter, index) => {
                      const first = index === 0;
                      return (
                        <div key={filter.title}
                          className={`custom-filter-container${index === 1 || index === 2 || index === 3 ? ' hidden' : ''}`}>
                          {first ? (
                            <>
                              <span className="select-none">Metadata</span>
                              <div onClick={(e) => openFilterDropdown(e.currentTarget.parentElement as HTMLElement, filter, true)}
                                className="user absolute top-0 left-0 right-0 bottom-0 bg-transparent"></div>
                            </>
                          ) : (!user && lockedFilters.includes(filter.title)) ? (
                            <section onClick={showSignUpPopup} className="flex">
                              <span title={filter.title} className="select-none flex-grow">{filter.title}
                              </span>
                              <svg aria-hidden="true" className="w-5 h-5 text-gray-800 dark:text-white"
                                fill="none"
                                height="20" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 14v3m-3-6V7a3 3 0 1 1 6 0v4m-8 0h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
                                  stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                                  strokeWidth="2" />
                              </svg>
                            </section>
                          ) : (
                            <>
                              <span className="select-none">{filter.title}</span>
                              <div onClick={(e) => openFilterDropdown(e.currentTarget.parentElement as HTMLElement, filter)}
                                className="user absolute top-0 left-0 right-0 bottom-0 bg-transparent"></div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {showBigSearchHomePage && (
                <>
                  {uploadStarted && (
                    <div className={`absolute top-[120px] md:top-[72px] w-full text-center ${PIXELATE_IN_OUT_ENTER_CLASS}`}>
                      <div className="flex justify-center items-center">
                        <p className="text-white">Finding music to bring your visuals to life. Hang tight!</p>
                      </div>
                      <div className="flex justify-center items-center">
                        {uploadProgress !== 100 ? (
                          <p className="text-white"> Upload is at - {uploadProgress}%</p>
                        ) : (
                          <p className="text-white">
                            Analyzing visual...
                          </p>
                        )}
                        <div className="ml-2 border-t-4 border-white border-solid rounded-full w-6 h-6 animate-spin"></div>
                      </div>
                    </div>
                  )}
                  {uploadFailed && !uploadStarted && !fileUploadResponse && (
                    <div className="absolute top-[120px] md:top-[72px] w-full text-center">
                      <p className="text-white">Upload failed. try again or try a different image/video.</p>
                      <p className="mt-1 text-sm text-mypart_red-300">{uploadError}</p>
                    </div>
                  )}
                  {fileUploadResponse && !uploadStarted && (
                    <div className={`absolute top-[75px] sm:top-[92px] w-full text-center px-4 ${PIXELATE_IN_OUT_DELAY_ENTER_CLASS}`}>
                      <p className="text-white text-lg sm:text-2xl font-bold">Pick a creative direction to find your song matches</p>
                      <p className="text-gray-300 text-sm sm:text-base mt-1">The top pick will start automatically — or choose another direction</p>
                    </div>
                  )}
                </>
              )}

            </form>

          </div>

        </div>

        {showBigSearchHomePage && showPromptCuratedPlaylists &&
          !fileUploadResponse && !uploadStarted && (
            <CuratedCarousel onPromptClicked={promptClicked}
              curatedPromptPlaylists={curatedPromptPlaylists}
              isMobileDevice={isMobileDevice}
              searchDropDownHidden={searchDropDownHidden} />
          )}

        {fileUploadResponse && !uploadStarted && showBigSearchHomePage && (
          <UploadResponses onSendToMain={sendToMain}
            fileUploadResponse={fileUploadResponse} />
        )}

        {/* Filters */}
        {filtersVisible && !showBigSearchHomePage && (
          <div ref={expandFadeInRef}
            className={`max-w-full relative z-[-1]${showBigSearchHomePage ? ' hidden' : ''}`}>
            <div ref={filtersScrollContainerRef} onScroll={onFiltersScroll}
              className="max-w-full flex items-center justify-between overflow-x-auto whitespace-nowrap
           pb-4 pt-2 results-filters-container filters-container">
              {filters.map((filter, index) => {
                const first = index === 0;
                const last = index === filters.length - 1;
                return (
                  <div key={filter.title}
                    className={`custom-filter-container text-gray-900 dark:text-white w-full min-w-fit md:min-w-[110px]
                      px-3 py-1.5 md:py-2.5 mr-4 relative
                      bg-white hover:bg-gray-100 dark:bg-zinc-800 dark:hover:bg-gray-700 rounded-lg${index === 1 || index === 2 || index === 3 ? ' hidden' : ''}${last ? ' !mr-0' : ''}`}>
                    {first ? (
                      <>
                        <span className="select-none">Metadata</span>
                        <div onClick={(e) => openFilterDropdown(e.currentTarget.parentElement as HTMLElement, filter, true)}
                          className="user absolute top-0 left-0 right-0 bottom-0 bg-transparent"></div>
                        {(filter.totalActiveFilters > 0 || filters[1].totalActiveFilters > 0 || filters[2].totalActiveFilters > 0 || filters[3].totalActiveFilters > 0) && (
                          <div onClick={() => clearSubFiltersFromMetadata(filters)}
                            className="absolute inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-500
                            border-2 border-white rounded-full -top-2 -right-2 dark:border-gray-900"
                            title="remove all filters from Metadata">
                            {filter.totalActiveFilters + filters[1].totalActiveFilters + filters[2].totalActiveFilters + filters[3].totalActiveFilters}
                          </div>
                        )}
                      </>
                    ) : (!user && lockedFilters.includes(filter.title)) ? (
                      <section onClick={showSignUpPopup} className="flex">
                        <span title={filter.title} className="select-none flex-grow">{filter.title}
                        </span>
                        <svg aria-hidden="true" className="w-5 h-5 text-gray-800 dark:text-white"
                          fill="none"
                          height="20" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 14v3m-3-6V7a3 3 0 1 1 6 0v4m-8 0h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
                            stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                            strokeWidth="2" />
                        </svg>
                      </section>
                    ) : (
                      <>
                        <span className="select-none">{filter.title}</span>
                        <div onClick={(e) => openFilterDropdown(e.currentTarget.parentElement as HTMLElement, filter)}
                          className="user absolute top-0 left-0 right-0 bottom-0 bg-transparent"></div>

                        {(filter.totalActiveFilters > 0 || (filter.title === 'Lyrical Theme' && lyricsContains)) && (
                          <div onClick={() => clearSubFiltersFromFilter(filter)}
                            className="absolute inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-500
                            border-2 border-white rounded-full -top-2 -right-2 dark:border-gray-900"
                            title={`remove all filters from ${filter.title}`}>
                            {totalActiveFiltersPipe(filter.totalActiveFilters, filter.title, lyricsContains)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {showScrollLeft && (
              <button onClick={() => scroll('left')}
                className="hidden md:flex absolute top-0 left-0 z-30 items-center justify-center h-full px-2 pl-0 pb-4 pt-2
             cursor-pointer group focus:outline-none"
                type="button">
                <span
                  className="inline-flex items-center justify-center w-6 h-6 rounded-full dark:bg-white/40 bg-gray-800/30
                   group-hover:bg-gray-800/60 dark:group-hover:bg-white/50 group-focus:ring-4 group-focus:ring-gray-800/70
                   dark:group-focus:ring-white group-focus:outline-none
                   dark:text-white text-gray-800 hover:text-white">
                  <svg aria-hidden="true" className="w-2.5 h-2.5" fill="none"
                    viewBox="0 0 6 10" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 1 1 5l4 4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                      strokeWidth="2" />
                  </svg>
                  <span className="sr-only">Scroll Left</span>
                </span>
              </button>
            )}
            {showScrollRight && (
              <button onClick={() => scroll('right')}
                className="hidden md:flex absolute top-0 right-0 z-30 items-center justify-center h-full px-2 pr-0 pb-4 pt-2
            cursor-pointer group focus:outline-none"
                type="button">
                <span
                  className="inline-flex items-center justify-center w-6 h-6 rounded-full dark:bg-white/30 bg-gray-800/30
                   group-hover:bg-gray-800/60 dark:group-hover:bg-white/50 group-focus:ring-4 group-focus:ring-gray-800/70
                   dark:group-focus:ring-white group-focus:outline-none
                   dark:text-white text-gray-800 hover:text-white">
                  <svg aria-hidden="true" className="w-2.5 h-2.5" fill="none"
                    viewBox="0 0 6 10" xmlns="http://www.w3.org/2000/svg">
                    <path d="m1 9 4-4-4-4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                      strokeWidth="2" />
                  </svg>
                  <span className="sr-only">Scroll Right</span>
                </span>
              </button>
            )}
          </div>
        )}
      </nav>

      {/* CDK Overlay replacement — the ported dropdowns portal to document.body. */}
      {openDropdown?.kind === 'filter' && (
        <FilterDropdown filter={openDropdown.filter}
          isInputFilter={openDropdown.isInputFilter}
          position={openDropdown.position}
          onFiltersClicked={debouncedFiltersClicked}
          onOutsideDropdownClick={closeOverlay} />
      )}
      {openDropdown?.kind === 'metadata' && (
        <MetadataFilterDropdown filters={metadataFilters}
          isInputFilter={openDropdown.isInputFilter}
          position={openDropdown.position}
          onFiltersClicked={debouncedFiltersClicked}
          onOutsideDropdownClick={closeOverlay} />
      )}
      {openDropdown?.kind === 'menu' && (
        <MenuDropdown position={openDropdown.position}
          onOutsideDropdownClick={closeOverlay} />
      )}
    </>
  );
}
